/**
 * SISTEMA DE PINNED THREADS EN LA UI (Single Source of Truth - SSOT)
 *
 * Qué es:
 * - Este componente NO reordena ni consulta directamente los "pinned".
 * - El ORDEN del thread list viene SIEMPRE definido por el ChatService (pinned primero).
 * - La foto de pinned (pinnedIds + pinnedOrder) y su actualización en tiempo real se gestionan en el servicio.
 *
 * Reglas de orden:
 * - Pinned SIEMPRE arriba del todo (pinned_order ASC y, en empate, updated_time DESC).
 * - Unpinned después (no leídos primero, luego updated_time DESC).
 * - Este componente respeta ese orden leyendo el cache ya ordenado del servicio.
 *
 * Cómo sincroniza:
 * - Boot: lee el snapshot de pinned con chatService.getPinnedSnapshot() (sin red).
 * - En tiempo real: se suscribe a 'pinned:updated' para reflejar cambios sin re-fetch.
 * - No dispara /threads ni /pinned-threads tras pin/unpin; la UI se mantiene via SSE + cache.
 *
 * Reglas de uso (IMPORTANTE):
 * - No ordenar manualmente el listado aquí.
 * - No llamar /instagram/pinned-threads desde este componente.
 * - Para pin/unpin usa useChat().togglePin(threadId, isPinned) (delegado al servicio).
 * - El servicio emite 'pinned:updated' y actualiza el cache de threads; este componente solo refleja estado.
 */
import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from "react";
import type { Message, Thread, Tag, TagFolder } from "../services/chatService";
import api, { fastapi } from "../services/api";
import clinicsApi from "../services/clinicsApi";
import chatService from "../services/chatService";
import { demoStore, isDemoModeActive } from "../services/demoModeService";
import { GhostSelect } from "./ui/GhostSelect";
import { GhostSwitch } from "./ui/GhostSwitch";
import { SlidingPills } from "./ui/SlidingPills";
import AppModal from "./ui/AppModal";
import MediaComposer from "./chat/MediaComposer";
import MessageMedia from "./chat/MessageMedia";
import ChatBotPopover from "./chat/ChatBotPopover";
import BtnTaratic from "./ui/BtnTaratic";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbtack, faUser, faRobot, faPowerOff, faList, faTimes, faCalendarCheck, faInfoCircle, faClock, faLock, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { TagBadge } from "./ui/TagBadge";
// Redux store hooks and chat actions
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  openChatUI,
  closeChatUI,
  selectThread as selectThreadThunk,
  sendMessage as sendMessageThunk,
  togglePin as togglePinThunk,
  setSoundEnabled as setSoundEnabledAction,
  setDocked as setDockedAction,
  refreshThreads as refreshThreadsThunk,
  loadMoreThreads,
  refreshMessages as refreshMessagesThunk,
  loadOlderMessages,
  prewarmTopMessages,
  markAsRead,
  mergeSearchThreads,
} from "../store/chat/chatSlice";
// RTK Query APIs for bots and tags (reads; mutations remain via chatService for now)
import {
  useGetBotsQuery,
  useGetChannelsStatusQuery,
  useGetActiveByChannelQuery,
  useActivateForChannelMutation,
  useClearActiveForChannelMutation,
} from "../store/bots/botsApi";
import type { Channel } from "../store/bots/botsApi";
import {
  useGetTagsWithFoldersQuery,
  useGetThreadTagsQuery,
} from "../store/tags/tagsApi";
import { getSecondsLeft, isExpired, formatPrettyLeft } from "../utils/jwt";
import { logoutThunk } from "../store/auth/authSlice";
import { useTranslation } from "react-i18next";
import { ThreadTagsSelector } from "./chat/ThreadTagsSelector";
import { ThreadNotesPanel } from "./chat/ThreadNotesPanel";
import { PatientLinkPanel } from "./chat/PatientLinkPanel";
import {
  getThreadClientUnreadCount,
  isBotlessThread,
  isHumanIntervention,
  summarizeChatNotifications,
} from "../utils/chatNotifications";
import {
  formatReplyWindowCountdown,
  getInstagramReplyWindowStatus,
  replyWindowEndsAtFromInbound,
} from "../utils/instagramReplyWindow";

// Skeleton components
const ThreadSkeleton: React.FC = memo(() => (
  <div data-ui="chat-thread-loading-placeholder" className="p-4 mx-2 my-1.5 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 border border-white/10 backdrop-blur-md animate-pulse relative overflow-hidden">
    <div data-ui="chat-thread-loading-content" className="flex items-center gap-4 relative z-10">
      <div data-ui="chat-sidebar-region-001" className="w-12 h-12 rounded-full bg-white/10 shadow-inner shrink-0" />
      <div data-ui="chat-sidebar-region-002" className="flex-1 min-w-0">
        <div data-ui="chat-sidebar-region-003" className="flex justify-between items-center mb-2.5">
          <div data-ui="chat-sidebar-region-004" className="h-3.5 bg-white/20 rounded-full w-28" />
          <div data-ui="chat-sidebar-region-005" className="h-2.5 bg-white/10 rounded-full w-8" />
        </div>
        <div data-ui="chat-sidebar-region-006" className="h-2.5 bg-white/10 rounded-full w-full mb-1.5" />
        <div data-ui="chat-sidebar-region-007" className="h-2.5 bg-white/10 rounded-full w-3/4" />
      </div>
    </div>
  </div>
));

const MessageSkeleton: React.FC = memo(() => (
  <div data-ui="chat-message-loading-placeholder" className="flex items-end gap-2 mb-3 animate-pulse">
    <div data-ui="chat-sidebar-region-008" className="w-8 h-8 bg-white/20 rounded-full" />
    <div data-ui="chat-sidebar-region-009" className="max-w-[70%]">
      <div data-ui="chat-sidebar-region-010" className="bg-white/20 rounded-2xl p-3">
        <div data-ui="chat-sidebar-region-011" className="h-3 bg-gray-300 rounded w-32 mb-2" />
        <div data-ui="chat-sidebar-region-012" className="h-3 bg-gray-300 rounded w-24" />
      </div>
    </div>
  </div>
));

const resolveThreadReplyWindowEndsAt = (thread?: Thread | null): string | null => {
  if (!thread) return null;
  if (thread.replyWindowEndsAt) return thread.replyWindowEndsAt;

  const lastSenderKind = String(thread.lastSenderKind || '').toLowerCase();
  const lastOrigin = String(thread.lastOrigin || '').toLowerCase();
  if (lastSenderKind === 'human' && lastOrigin === 'instagram') {
    return replyWindowEndsAtFromInbound(thread.updated_time);
  }
  return null;
};

const useReplyWindowBlocked = (
  replyWindowEndsAt?: string | null,
  platform?: string | null,
) => {
  const [, forceDeadlineRefresh] = useState(0);

  useEffect(() => {
    const status = getInstagramReplyWindowStatus(replyWindowEndsAt, platform);
    if (status.kind !== 'open') return;

    const timer = window.setTimeout(
      () => forceDeadlineRefresh((revision) => revision + 1),
      Math.min(status.remainingMs + 25, 2_147_483_647),
    );
    return () => window.clearTimeout(timer);
  }, [replyWindowEndsAt, platform]);

  return getInstagramReplyWindowStatus(replyWindowEndsAt, platform).kind === 'blocked';
};

const ReplyWindowCountdown: React.FC<{
  replyWindowEndsAt?: string | null;
  platform?: string | null;
  blocked?: boolean;
}> = ({ replyWindowEndsAt, platform, blocked: blockedByComposer = false }) => {
  const { t } = useTranslation();
  const [nowMs, setNowMs] = useState(Date.now());
  const status = getInstagramReplyWindowStatus(replyWindowEndsAt, platform, nowMs);

  useEffect(() => {
    const initial = getInstagramReplyWindowStatus(replyWindowEndsAt, platform);
    setNowMs(Date.now());
    if (initial.kind !== 'open') return;
    const timer = window.setInterval(() => {
      const nextNow = Date.now();
      setNowMs(nextNow);
      if (getInstagramReplyWindowStatus(replyWindowEndsAt, platform, nextNow).kind === 'blocked') {
        window.clearInterval(timer);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [replyWindowEndsAt, platform]);

  if (status.kind === 'unknown' || status.kind === 'not-applicable') return null;

  const blocked = blockedByComposer || status.kind === 'blocked';
  const countdown = formatReplyWindowCountdown(blocked ? 0 : status.remainingMs);
  const urgent = !blocked && status.remainingMs <= 60 * 60 * 1000;

  return (
    <div
      data-ui="chat-instagram-reply-window"
      data-status={blocked ? 'blocked' : 'open'}
      className={`mb-2 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${
        blocked
          ? 'border-red-400/40 bg-red-500/15 text-red-200'
          : urgent
            ? 'border-amber-400/40 bg-amber-500/15 text-amber-100'
            : 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100'
      }`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <FontAwesomeIcon icon={blocked ? faLock : faClock} className="shrink-0" />
      <span className="font-bold tracking-wide">
        {blocked ? t('chatSidebar.replyWindow.blocked') : countdown}
      </span>
      <span className="opacity-80">
        {blocked
          ? t('chatSidebar.replyWindow.blockedDescription')
          : t('chatSidebar.replyWindow.remaining')}
      </span>
    </div>
  );
};

// Thread item component
interface ThreadItemProps {
  thread: Thread;
  isSelected: boolean;
  isPinned: boolean;
  onSelect: () => void;
  onTogglePin: () => void;
  disabled?: boolean;
  botMeta?: {
    botEnabled: boolean | null;
    botType: string | null;
    botName: string | null;
    botId: number | null;
    escalatedToHuman: boolean;
    escalationReason: string | null;
  };
  channelDefaultBotId?: number | null;
  tags?: Tag[];
}

const ACTIVE_THREAD_BOT_TAG_CLASSES =
  "text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 transition-all border border-white/20 bg-black text-white shadow-inner-glow shadow-inner-glow-white-2xl hover:bg-black";

const ThreadItem: React.FC<ThreadItemProps> = memo(
  ({
    thread,
    isSelected,
    isPinned,
    onSelect,
    onTogglePin,
    disabled = false,
    botMeta,
    channelDefaultBotId,
    tags,
  }) => {
    const { t } = useTranslation();

    const timeAgo = (date: string) => {
      const now = new Date();
      const then = new Date(date);
      const diff = now.getTime() - then.getTime();
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return "ahora";
      if (minutes < 60) return `${minutes}m`;
      if (hours < 24) return `${hours}h`;
      if (days < 7) return `${days}d`;
      return then.toLocaleDateString("es-ES", {
        day: "numeric",
        month: "short",
      });
    };

    // Normalize unread count for this thread (supports legacy boolean 'unread')
    const unreadN = Math.max(
      0,
      Number((thread as any).unreadCount ?? ((thread as any).unread ? 1 : 0)) ||
      0
    );

    // Per-kind unseen counters (maintained by ChatService cache)
    const unreadClient = getThreadClientUnreadCount(thread);
    const unreadBot = Math.max(0, Number((thread as any).unreadBotCount || 0));
    const lastBotPreview: string | null = (thread as any).lastBotPreview || null;
    const lastClientPreview: string | null = (thread as any).lastClientPreview || null;

    // Last message meta to reflect bot/automation in thread list even when unread stays 0
    const lastSenderKind = String(
      (thread as any).lastSenderKind ?? (thread as any).last_sender_kind ?? ""
    ).toLowerCase();
    const lastOrigin = String(
      (thread as any).lastOrigin ?? (thread as any).last_origin ?? ""
    ).toLowerCase();
    const lastBotName =
      (thread as any).lastBotName ??
      (thread as any).last_bot_name ??
      null;
    const botIsLatest = !!lastBotPreview && (thread.lastMessage === lastBotPreview);

    const isBotish =
      lastSenderKind === "bot" ||
      lastSenderKind === "auto" ||
      lastOrigin === "taratic_api" ||
      botIsLatest;
    const notificationThread: Thread = {
      ...thread,
      bot_enabled: botMeta?.botEnabled ?? thread.bot_enabled,
      selected_bot_id: botMeta?.botId ?? thread.selected_bot_id,
      escalated_to_human: botMeta?.escalatedToHuman ?? thread.escalated_to_human,
      escalation_reason: botMeta?.escalationReason ?? thread.escalation_reason,
    };
    const humanIntervention = isHumanIntervention(notificationThread);
    const botless = isBotlessThread(notificationThread, channelDefaultBotId);
    const replyWindowEndsAt = resolveThreadReplyWindowEndsAt(thread);
    const replyWindowBlocked = useReplyWindowBlocked(replyWindowEndsAt, thread.platform);

    /** 
     * THREAD ROW
    */
    return (
      <div
        data-ui={`chat-thread-${thread.id}`}
        data-state={isSelected ? 'selected' : 'idle'}
        className={`
        relative flex flex-col transition-all
        ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-black/10 active:bg-white/10"}
        ${isSelected ? "bg-black hover:bg-black" : ""}
        ${unreadN > 0 ? "font-semibold" : ""}
      `}
        aria-disabled={disabled}
        onMouseEnter={() => {
          try { (chatService as any).getMessages?.(thread.id, false); } catch { }
        }}
        onTouchStart={() => {
          try { (chatService as any).getMessages?.(thread.id, false); } catch { }
        }}
      >
        <div data-ui="chat-thread-summary" className="flex items-center gap-3 px-3 pt-3 pb-1 w-full" onClick={() => { if (!disabled) onSelect(); }}>
          {/* Avatar with online indicator */}
          <div data-ui="chat-thread-avatar" className="relative flex-shrink-0">
            {thread.avatarUrl ? (
              <>
                <img
                  src={thread.avatarUrl}
                  alt={thread.name}
                  className="w-12 h-12 rounded-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    if (e.currentTarget.nextElementSibling) {
                      e.currentTarget.nextElementSibling.classList.remove('hidden');
                      e.currentTarget.nextElementSibling.classList.add('flex');
                    }
                  }}
                />
                <div data-ui="chat-sidebar-region-013" className="hidden w-12 h-12 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full items-center justify-center text-white font-semibold">
                  {thread.name.charAt(0).toUpperCase()}
                </div>
              </>
            ) : (
              <div data-ui="chat-sidebar-region-014" className="w-12 h-12 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                {thread.name.charAt(0).toUpperCase()}
              </div>
            )}
            {thread.online && (
              <div data-ui="chat-sidebar-region-015" className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full" />
            )}
          </div>

          {/* Content */}
          <div data-ui="chat-thread-details" className="flex-1 min-w-0">
            <div data-ui="chat-sidebar-region-016" className="flex items-center justify-between mb-0.5">
              <div data-ui="chat-sidebar-region-017" className="min-w-0 flex flex-col gap-0.5">
                {thread.username && (
                  <span data-ui="chat-thread-username" className="text-sm text-white truncate max-w-full">
                    @{thread.username}
                  </span>
                )}
                <span
                  data-ui="chat-thread-display-name"
                  className="truncate text-white"
                >
                  {thread.name}
                </span>
                {thread.patient && (
                  <div data-ui="chat-thread-patient" className="flex items-center gap-1 text-[11px] text-blue-300" title={thread.patient.full_name}>
                    <FontAwesomeIcon icon={faUser} className="text-[10px]" />
                    <span className="truncate max-w-[120px]">{thread.patient.full_name}</span>
                  </div>
                )}
                {/* Show the assigned bot only while it is actually enabled. */}
                {botMeta?.botEnabled === true && botMeta.botName && (
                  <span data-ui="chat-thread-assigned-bot" className={ACTIVE_THREAD_BOT_TAG_CLASSES}>
                    🤖 {botMeta.botName}
                  </span>
                )}
              </div>
              <div data-ui="chat-sidebar-region-019" className="flex items-center gap-1 flex-shrink-0 ml-2">
                <span
                  data-ui="chat-thread-time"
                  className={`text-xs ${unreadN > 0 ? "text-blue-600" : "text-white"}`}
                >
                  {timeAgo(thread.updated_time)}
                </span>
              </div>
            </div>
            {/* Bot Pausado Indicator */}
            {replyWindowBlocked && (
              <div data-ui="chat-thread-reply-window-status" className="mt-1 mb-1">
                <span
                  data-ui="chat-thread-blocked-badge"
                  className="inline-flex items-center gap-1.5 rounded border border-red-400/40 bg-red-500/60 px-2 py-0.5 text-[10px] font-bold tracking-wide text-red-300 [text-shadow:0_1px_1px_#000] shadow-[0_0_8px_rgba(248,113,113,0.25)]"
                  title={t('chatSidebar.replyWindow.blockedDescription')}
                >
                  <FontAwesomeIcon icon={faLock} className="text-[9px]" />
                  {t('chatSidebar.replyWindow.blocked')}
                </span>
              </div>
            )}
            {humanIntervention && (
              <div data-ui="chat-sidebar-region-020" className="mt-1 mb-1">
                <span data-ui="chat-thread-human-intervention-badge" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold bg-yellow-500/20 text-yellow-500 border border-yellow-500/30 shadow-[0_0_8px_rgba(234,179,8,0.3)]">
                  <FontAwesomeIcon icon={faUser} className="text-[10px]" />
                  {t('chatSidebar.thread.botPausedHumanIntervention')}
                </span>
              </div>
            )}
            {/* Cita Agendada Indicator */}
            {(thread as any).appointment_booked && (
              <div data-ui="chat-sidebar-region-021" className="mt-1 mb-1">
                <span data-ui="chat-thread-appointment-badge" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shadow-[0_0_8px_rgba(16,185,129,0.3)]">
                  <FontAwesomeIcon icon={faCalendarCheck} className="text-[10px]" />
                  {t('chatSidebar.thread.appointmentScheduledByAI')}
                </span>
              </div>
            )}
            {Array.isArray(tags) && tags.length > 0 && (
              <div data-ui="chat-sidebar-region-022" className="mt-1 flex flex-wrap gap-1">
                {tags.slice(0, 3).map((tag: any) => (
                  <TagBadge
                    key={tag.id || tag.tag_id}
                    tag={tag}
                    size="xs"
                    className="chat-thread-tag"
                  />
                ))}
                {tags.length > 3 && (
                  <span className="text-[10px] text-white px-1.5">
                    +{tags.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div data-ui="chat-thread-actions" className="flex items-center gap-1.5 pl-2 flex-shrink-0">
            {/* Client unseen: orange without a bot, otherwise red */}
            {unreadClient > 0 && (
              <div data-ui="chat-sidebar-region-023"
                className={`min-w-[22px] h-[22px] px-1.5 text-white text-[11px] font-bold rounded-full flex items-center justify-center border border-white/20 backdrop-blur-md transition-all duration-300 hover:scale-110 ${botless
                  ? "bg-gradient-to-br from-orange-400 to-orange-600 shadow-[0_0_12px_rgba(249,115,22,0.55)]"
                  : "bg-gradient-to-br from-red-500 to-rose-600 shadow-[0_0_12px_rgba(225,29,72,0.4)]"
                  }`}
                title={botless ? "Mensajes sin bot disponibles" : "Mensajes del cliente sin revisar"}
              >
                {unreadClient > 99 ? "99+" : unreadClient}
              </div>
            )}
            {/* Bot/auto unseen: Gemini 2026 Violet */}
            {unreadBot > 0 && (
              <div data-ui="chat-sidebar-region-024"
                className="min-w-[22px] h-[22px] px-1.5 bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white text-[11px] font-bold rounded-full flex items-center justify-center border border-white/20 shadow-[0_0_12px_rgba(139,92,246,0.4)] backdrop-blur-md transition-all duration-300 hover:scale-110"
                title="Respuestas del bot sin revisar"
              >
                {unreadBot > 99 ? "99+" : unreadBot}
              </div>
            )}
            <BtnTaratic
              onClick={(e) => {
                e.stopPropagation();
                onTogglePin();
              }}
              color={isPinned ? "blue" : "ghost-dark"}
              className="w-7 h-7 flex items-center justify-center p-0 rounded-lg flex-shrink-0"
              title={isPinned ? "Desanclar" : "Anclar"}
            >
              <FontAwesomeIcon icon={faThumbtack} className="text-sm" />
            </BtnTaratic>
          </div>
        </div>

        {/* Previews Row (Full Width below Avatar & Tags) */}
        <div data-ui="chat-thread-preview" className="px-3 pb-3 w-full" onClick={() => { if (!disabled) onSelect(); }}>
          {/* Bot Preview Wrapper */}
          <div data-ui="chat-sidebar-region-025" className="flex items-center gap-1 min-w-0 w-full">
            {thread.typing ? (
              <div data-ui="chat-sidebar-region-026" className="flex items-center gap-1 text-white">
                <span className="text-sm italic">escribiendo</span>
                <div data-ui="chat-sidebar-region-027" className="flex gap-0.5">
                  <span
                    className="w-1 h-1 bg-black/100 rounded-full animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  />
                  <span
                    className="w-1 h-1 bg-black/100 rounded-full animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  />
                  <span
                    className="w-1 h-1 bg-black/100 rounded-full animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  />
                </div>
              </div>
            ) : (
              <div data-ui="chat-sidebar-region-028" className="flex flex-col gap-1 mt-1 min-w-0 w-full">
                {/* Bot Preview - Speech Bubble ("Bocadillo") Style */}
                {(unreadBot > 0 && lastBotPreview) || isBotish ? (
                  <div data-ui="chat-sidebar-region-029" className="w-full my-1 min-w-0 pr-8 sm:pr-10">
                    <div data-ui="chat-thread-bot-preview"
                      className="inline-flex items-center gap-1.5 max-w-full min-w-0 px-3 py-1 rounded-2xl rounded-tl-xs bg-gradient-to-r from-purple-900/50 to-fuchsia-900/40 border border-purple-500/40 shadow-[0_0_10px_rgba(168,85,247,0.2)] text-purple-200 backdrop-blur-md overflow-hidden"
                      title={lastBotName ? `Bot: ${lastBotName}` : "Mensaje del Bot"}
                    >
                      <span data-ui="chat-thread-bot-preview-label" className="flex-shrink-0 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded-md bg-purple-500/30 text-purple-200 border border-purple-400/40">
                        🤖 {lastSenderKind === "auto" ? "AUTO" : "BOT"}
                      </span>
                      <p data-ui="chat-thread-bot-preview-text" className="text-[12.5px] truncate min-w-0 flex-1 text-purple-100 font-medium leading-tight">
                        {lastBotPreview || thread.lastMessage || "Sin mensajes"}
                      </p>
                    </div>
                  </div>
                ) : null}

                {/* Client Preview - Clean & Sharp */}
                {(!isBotish || (lastBotPreview && lastClientPreview)) && (
                  <div data-ui="chat-sidebar-region-031" className="flex items-center gap-1.5 w-full">
                    <p
                      data-ui="chat-thread-client-preview"
                      className={`text-[13px] truncate leading-relaxed ${unreadClient > 0 || unreadN > 0
                        ? "text-white font-semibold"
                        : "text-white/70 font-normal"
                        }`}
                    >
                      {lastClientPreview || (!isBotish && thread.lastMessage) || "Sin mensajes"}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

// Message component
interface MessageItemProps {
  message: Message;
  showAvatar: boolean;
  isLastFromSender: boolean;
  threadAvatarUrl?: string | null;
  threadName?: string;
}

const MessageItem: React.FC<MessageItemProps> = memo(
  ({ message, showAvatar, isLastFromSender, threadAvatarUrl, threadName }) => {
    const formatTime = (date: string) => {
      return new Date(date).toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    // Determine message style based on sender type
    const getMessageStyle = () => {
      if (!message.fromMe) {
        return "bg-white/10 text-white rounded-bl-sm";
      }

      // All messages from the clinic (human or bot) - Instagram parallax effect (lilac and pink)
      return "bg-gradient-to-br from-purple-700 to-pink-700 bg-fixed text-white rounded-br-xl";
    };

    const getSenderLabel = () => {
      let label = "";

      // Add sender type with bot name if available
      if (message.senderKind === "bot") {
        // Use specific bot name if available, otherwise generic
        if ((message as any).botName) {
          // Format bot name nicely
          const botLabels: Record<string, string> = {
            taratic_api: "🤖 taratic_api Bot",
            chatgpt: "🧠 ChatGPT",
            gemini: "✨ Gemini",
            claude: "🎓 Claude",
            custom: "🔧 Custom Bot",
          };
          label =
            botLabels[(message as any).botName] ||
            `🤖 ${(message as any).botName}`;
        } else {
          label = "🤖 Bot";
        }
      } else if (message.senderKind === "auto") {
        label = "⚡ Automático";
      } else if (message.senderKind === "human" || message.senderKind === "operator") {
        label = "👤 Manual";
      }

      // Add origin if different from web and instagram (default)
      if (
        message.origin &&
        message.origin !== "web" &&
        message.origin !== "instagram"
      ) {
        const originLabels: Record<string, string> = {
          pedidos: "🛒 Pedidos",
          reservas: "📅 Reservas",
          taratic_api: "⚙️ taratic_api",
          api: "🔌 API",
        };
        const originLabel = originLabels[message.origin] || message.origin;
        label = label ? `${label} • ${originLabel}` : originLabel;
      }

      return label || null;
    };

    return (
      <div
        data-ui={`chat-message-${message.id}`}
        data-message-direction={message.fromMe ? 'outgoing' : 'incoming'}
        className={`flex items-end gap-2 mb-1 w-full ${message.fromMe ? "justify-end" : "justify-start"}`}
      >
        {/* Avatar for received messages */}
        {!message.fromMe &&
          (showAvatar ? (
            (message.avatarUrl || threadAvatarUrl) ? (
              <>
                <img
                  src={(message.avatarUrl || threadAvatarUrl) as string}
                  alt="Avatar"
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    if (e.currentTarget.nextElementSibling) {
                      e.currentTarget.nextElementSibling.classList.remove('hidden');
                      e.currentTarget.nextElementSibling.classList.add('flex');
                    }
                  }}
                />
                <div data-ui="chat-sidebar-region-032" className="hidden w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 text-white items-center justify-center text-xs font-semibold flex-shrink-0">
                  {(threadName || "").charAt(0).toUpperCase()}
                </div>
              </>
            ) : (
              <div data-ui="chat-sidebar-region-033" className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                {(threadName || "").charAt(0).toUpperCase()}
              </div>
            )
          ) : (
            <div data-ui="chat-sidebar-region-034" className="w-8 h-8 invisible" />
          ))}

        {/* Message bubble */}
        <div
          data-ui="chat-message-bubble-wrapper"
          className={`
          relative max-w-[70%] min-w-0 group
          ${isLastFromSender ? "mb-3" : ""}
        `}
        >
          {/* Sender label above message for all sent messages with info */}
          {message.fromMe && getSenderLabel() && (
            <div data-ui="chat-sidebar-region-035" className="text-xs text-white mb-1 text-right">
              {getSenderLabel()}
            </div>
          )}

          <div
            data-ui="chat-message-bubble"
            data-ui-variant={message.fromMe ? "primary-text-bubble" : "alternative-text-bubble"}
            className={`
            px-3 py-2 rounded-2xl break-words
            ${getMessageStyle()}
            ${message.status === "error" ? "opacity-50" : ""}
          `}
          >
            {/* Message content */}
            {message.text && (
              <p className="whitespace-pre-wrap break-words leading-relaxed">
                {message.text}
              </p>
            )}
            <MessageMedia message={message} />

            {/* Time and status */}
            <div data-ui="chat-sidebar-region-036"
              className={`
              flex items-center gap-1 mt-1 text-xs text-white
            `}
            >
              <span>{formatTime(message.createdAt)}</span>
              {message.fromMe && (
                <>
                  <span className="ml-1">
                    {message.status === "sending" && "⏱"}
                    {message.status === "sent" && "✓"}
                    {message.status === "delivered" && "✓✓"}
                    {message.status === "read" && (
                      <span
                        className={
                          message.senderKind === "human"
                            ? "text-blue-300"
                            : "text-purple-300"
                        }
                      >
                        ✓✓
                      </span>
                    )}
                    {message.status === "error" && "❌"}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Error retry button */}
          {message.status === "error" && (
            <button className="absolute -bottom-6 right-0 text-xs text-red-500 hover:underline">
              Reintentar
            </button>
          )}
        </div>
      </div>
    );
  },
  (prev, next) => {
    const a = prev.message,
      b = next.message;
    return (
      a.id === b.id &&
      a.fromMe === b.fromMe &&
      a.text === b.text &&
      a.imageUrl === b.imageUrl &&
      JSON.stringify(a.attachments) === JSON.stringify(b.attachments) &&
      a.createdAt === b.createdAt &&
      a.status === b.status &&
      a.senderKind === b.senderKind &&
      a.origin === b.origin &&
      (a as any).botName === (b as any).botName &&
      prev.showAvatar === next.showAvatar &&
      prev.isLastFromSender === next.isLastFromSender &&
      prev.threadAvatarUrl === next.threadAvatarUrl &&
      prev.threadName === next.threadName
    );
  }
);

// Main ChatSidebar component
const ChatSidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // Chat slice state
  const isOpen = useAppSelector((s) => s.chat.isOpen);
  const theme = useAppSelector((s) => s.ui.theme);
  const selectedThreadId = useAppSelector((s) => s.chat.selectedThreadId);
  const threads = useAppSelector((s) => s.chat.threads);
  const messages = useAppSelector((s) => s.chat.messages);
  const unreadCount = useAppSelector((s) => s.chat.unreadCount);
  const isConnected = useAppSelector((s) => s.chat.isConnected);
  const connectionStatus = useAppSelector((s) => s.chat.connectionStatus);
  const threadsLoading = useAppSelector((s) => s.chat.threadsLoading);
  const threadsLoadFailed = useAppSelector((s) => s.chat.threadsLoadFailed);
  const threadsHasMore = useAppSelector((s) => s.chat.threadsHasMore);
  const threadsLoadingMore = useAppSelector((s) => s.chat.threadsLoadingMore);
  const lastThreadsRefreshAt = useAppSelector((s) => s.chat.lastThreadsRefreshAt);
  const messagesLoading = useAppSelector((s) => s.chat.messagesLoading);
  const switchingThread = useAppSelector((s) => s.chat.switchingThread);
  const loadingOlder = useAppSelector((s) => s.chat.loadingOlder);
  const nextMessagesCursor = useAppSelector((s) => s.chat.nextCursor);
  const sendingMessage = useAppSelector((s) => s.chat.sendingMessage);
  const soundEnabled = useAppSelector((s) => s.chat.soundEnabled);
  const isDocked = useAppSelector((s) => s.chat.isDocked);
  const typingByThread = useAppSelector((s) => s.chat.typingByThread);
  const firstNewMessageId = useAppSelector((s) => s.chat.firstNewMessageId);

  // Derive typing Set<string> for compatibility with previous UI checks
  const typingThreads = useMemo(() => {
    const set = new Set<string>();
    try {
      Object.entries(typingByThread || {}).forEach(([k, v]) => {
        if (v) set.add(k);
      });
    } catch { }
    return set;
  }, [typingByThread]);

  // Action wrappers (parity with previous context API)
  const openChat = useCallback(() => { dispatch(openChatUI()); }, [dispatch]);
  const closeChat = useCallback(() => { dispatch(closeChatUI()); }, [dispatch]);
  const selectThread = useCallback((id: string | null) => { dispatch(selectThreadThunk(id)); }, [dispatch]);
  const sendMessage = useCallback((text?: string, imageUrl?: string, options?: any) => {
    return dispatch(sendMessageThunk({ text, imageUrl, options } as any)).unwrap();
  }, [dispatch]);
  const togglePin = useCallback((threadId: string, isPinned: boolean) => {
    return dispatch(togglePinThunk({ threadId, isPinned })).unwrap();
  }, [dispatch]);
  const setSoundEnabled = useCallback((enabled: boolean) => { dispatch(setSoundEnabledAction(enabled)); }, [dispatch]);
  const setDocked = useCallback((docked: boolean) => { dispatch(setDockedAction(docked)); }, [dispatch]);
  const refreshThreads = useCallback(() => dispatch(refreshThreadsThunk()).unwrap(), [dispatch]);
  const loadMoreConversationThreads = useCallback(() => dispatch(loadMoreThreads()).unwrap(), [dispatch]);
  const refreshMessages = useCallback(() => dispatch(refreshMessagesThunk()).unwrap(), [dispatch]);

  // Local state
  const [messageText, setMessageText] = useState("");
  const [senderKind, setSenderKind] = useState<"human" | "bot" | "auto">(
    "human"
  );
  const [botName, setBotName] = useState<string>(""); // Estado para el nombre del bot
  // Origin es siempre 'web' cuando se envía desde el frontend
  const origin = "web";
  const [mediaOpen, setMediaOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 640
  );
  const [showMobileThreads, setShowMobileThreads] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchNextCursor, setSearchNextCursor] = useState<string | null>(null);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchLoadingMore, setSearchLoadingMore] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());
  const [forceReloading, setForceReloading] = useState(false);
  const [botStatusFilter, setBotStatusFilter] = useState<'all' | 'bot_active' | 'human_intervention' | 'no_bot' | 'appointment'>('all');

  // JWT status and auto-logout (security + UX)
  const [jwtSecondsLeft, setJwtSecondsLeft] = useState<number | null>(null);
  const [jwtExpired, setJwtExpired] = useState(false);
  const jwtAutoLogoutRef = useRef(false);

  useEffect(() => {
    const compute = () => {
      const t = typeof window !== 'undefined' ? localStorage.getItem('jwtToken') : null;
      setJwtSecondsLeft(getSecondsLeft(t));
      // small skew to avoid race at exact boundary
      setJwtExpired(isExpired(t, 5));
    };
    compute();
    const id = window.setInterval(compute, 15000);
    const onLogin = () => compute();
    const onLogout = () => compute();
    window.addEventListener('auth:login', onLogin);
    window.addEventListener('auth:logout', onLogout);
    return () => {
      clearInterval(id);
      window.removeEventListener('auth:login', onLogin);
      window.removeEventListener('auth:logout', onLogout);
    };
  }, []);

  // Proactive auto-logout when token is expired (prevents any action with stale/stolen token)
  useEffect(() => {
    if (jwtExpired && !jwtAutoLogoutRef.current) {
      jwtAutoLogoutRef.current = true;
      (async () => {
        try {
          await dispatch(logoutThunk()).unwrap();
        } catch { }
        try { window.location.href = '/login'; } catch { }
      })();
    }
  }, [jwtExpired, dispatch]);


  // Filters (platform + AND tags) and tags/folders data
  const [showFilter, setShowFilter] = useState(false);
  const [platformFilter, setPlatformFilter] = useState<'instagram'>('instagram');
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [availableFolders, setAvailableFolders] = useState<TagFolder[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [allowedIds, setAllowedIds] = useState<Set<string> | null>(null);

  // Search all locally indexed conversations after a short debounce, while the
  // current page stays instant. Abort stale requests as the user keeps typing.
  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchNextCursor(null);
      setSearchHasMore(false);
      setSearchLoadingMore(false);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        if (isDemoModeActive()) {
          const normalized = query.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
          const results = demoStore.threads().filter(thread => [thread.name, thread.username, thread.lastMessage]
            .some(value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(normalized)));
          setSearchNextCursor(null);
          setSearchHasMore(false);
          dispatch(mergeSearchThreads(results));
          return;
        }
        const { data } = await api.get('/v1/chat/threads/search', {
          params: { q: query, limit: 50 },
          signal: controller.signal,
        });
        const raw = Array.isArray(data) ? data : (data?.items || data?.data || []);
        if (controller.signal.aborted || !Array.isArray(raw)) return;
        const results = raw.map((thread: any) => ({
          id: String(thread.id),
          customer_id: thread.customer_id,
          name: thread.name || 'Cliente',
          username: thread.username || null,
          lastMessage: thread.lastMessage || thread.last_message || '',
          unreadCount: Number(thread.unreadCount ?? thread.unread_count ?? 0),
          unreadClientCount: thread.unreadClientCount ?? thread.unread_client_count,
          unreadBotCount: thread.unreadBotCount ?? thread.unread_bot_count,
          unread: Boolean(thread.unread ?? thread.unreadCount ?? thread.unread_count),
          updated_time: thread.updated_time || '',
          platform: thread.platform || 'instagram',
          replyWindowEndsAt: thread.replyWindowEndsAt || thread.reply_window_ends_at || null,
          avatarUrl: thread.avatarUrl || thread.avatar_url || null,
          patient: thread.patient || null,
          bot_enabled: thread.bot_enabled,
          selected_bot_id: thread.selected_bot_id ?? null,
          escalated_to_human: Boolean(thread.escalated_to_human),
          escalation_reason: thread.escalation_reason ?? null,
        })) as Thread[];
        setSearchNextCursor(data?.next_cursor ? String(data.next_cursor) : null);
        setSearchHasMore(Boolean(data?.has_more));
        dispatch(mergeSearchThreads(results));
      } catch (error: any) {
        // Cancellations are expected while typing; retain instant local matches.
        if (error?.code !== 'ERR_CANCELED' && error?.name !== 'CanceledError') {
          // Keep local matches visible when the search index is temporarily unavailable.
        }
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery, dispatch]);

  const loadMoreSearchResults = useCallback(async () => {
    const query = searchQuery.trim();
    if (!query || !searchHasMore || !searchNextCursor || searchLoadingMore) return;
    setSearchLoadingMore(true);
    try {
      if (isDemoModeActive()) { setSearchHasMore(false); return; }
      const { data } = await api.get('/v1/chat/threads/search', {
        params: { q: query, limit: 50, cursor: searchNextCursor },
      });
      const raw = Array.isArray(data) ? data : (data?.items || data?.data || []);
      if (!Array.isArray(raw)) return;
      const results = raw.map((thread: any) => ({
        id: String(thread.id), customer_id: thread.customer_id, name: thread.name || 'Cliente',
        username: thread.username || null, lastMessage: thread.lastMessage || thread.last_message || '',
        unreadCount: Number(thread.unreadCount ?? thread.unread_count ?? 0),
        unreadClientCount: thread.unreadClientCount ?? thread.unread_client_count,
        unreadBotCount: thread.unreadBotCount ?? thread.unread_bot_count,
        unread: Boolean(thread.unread ?? thread.unreadCount ?? thread.unread_count),
        updated_time: thread.updated_time || '', avatarUrl: thread.avatarUrl || thread.avatar_url || null,
        platform: thread.platform || 'instagram',
        replyWindowEndsAt: thread.replyWindowEndsAt || thread.reply_window_ends_at || null,
        patient: thread.patient || null, bot_enabled: thread.bot_enabled,
        selected_bot_id: thread.selected_bot_id ?? null,
        escalated_to_human: Boolean(thread.escalated_to_human),
        escalation_reason: thread.escalation_reason ?? null,
      })) as Thread[];
      dispatch(mergeSearchThreads(results));
      setSearchNextCursor(data?.next_cursor ? String(data.next_cursor) : null);
      setSearchHasMore(Boolean(data?.has_more));
    } finally {
      setSearchLoadingMore(false);
    }
  }, [searchQuery, searchHasMore, searchNextCursor, searchLoadingMore, dispatch]);

  // Selected thread tags + add flow
  const [threadTags, setThreadTags] = useState<Tag[]>([]);


  // IA drafts UI state
  const [showAIDrafts, setShowAIDrafts] = useState(false);
  const [aiDraftsLoading, setAIDraftsLoading] = useState(false);
  const [aiSuggestions, setAISuggestions] = useState<string[]>([]);
  const [aiError, setAIError] = useState<string | null>(null);
  const [aiSelectedBotId, setAISelectedBotId] = useState<number | "">("");
  const [aiUsedBotName, setAIUsedBotName] = useState<string | null>(null);
  const [demoGeneratingRole, setDemoGeneratingRole] = useState<'customer' | 'operator' | null>(null);

  // Derive comercioKey early for thread meta fetch
  const comercioKey =
    connectionStatus?.comercio_key ||
    (typeof window !== "undefined" ? localStorage.getItem("comercio_key") : "") ||
    "";

  // Bots via RTK Query
  const { data: bots = [], isFetching: botsLoading, refetch: refetchBots } = useGetBotsQuery();
  const { data: channelsStatus = { instagram: false, whatsapp: false, email: false }, refetch: refetchChannels } =
    useGetChannelsStatusQuery();
  const {
    data: activeByChannel = { instagram: null, whatsapp: null, email: null },
    isSuccess: activeByChannelLoaded,
    refetch: refetchActive,
  } =
    useGetActiveByChannelQuery();
  const channelDefaultBotId = activeByChannelLoaded
    ? activeByChannel.instagram?.id ?? null
    : undefined;
  const [activateForChannelCtx] = useActivateForChannelMutation();
  const [clearActiveForChannelCtx] = useClearActiveForChannelMutation();

  // Tags via RTK Query
  const { data: tagsWithFolders } = useGetTagsWithFoldersQuery();

  // Unified refresh for bots panel
  const refreshAll = useCallback(async () => {
    await Promise.all([refetchBots(), refetchChannels(), refetchActive()]);
  }, [refetchBots, refetchChannels, refetchActive]);

  // Per-thread bot metadata cache for the thread list (effective bot type/name and on/off)
  const [threadBotMeta, setThreadBotMeta] = useState<
    Record<
      string,
      {
        botEnabled: boolean | null;
        botType: string | null;
        botName: string | null;
        botId: number | null;
        escalatedToHuman: boolean;
        escalationReason: string | null;
      }
    >
  >({});
  // [SYNC-NOTE]: Este cálculo lee de `threadBotMeta` para reflejar actualizaciones UI instantáneas
  // al pulsar botones de Reactivar. Además `threadBotMeta` se mantiene en sincronía con Redux (vía SSE)
  // mediante un useEffect más abajo. Debe mantener la misma lógica base que el de Sidebar.tsx.
  // One shared classification keeps red unread, orange botless, and yellow
  // intervention badges mutually exclusive across every chat surface.
  const notificationThreads = useMemo(
    () => (Array.isArray(threads) ? threads : []).map((thread) => {
      const meta = threadBotMeta[thread.id];
      return {
        ...thread,
        bot_enabled: meta?.botEnabled ?? thread.bot_enabled,
        selected_bot_id: meta?.botId ?? thread.selected_bot_id,
        escalated_to_human: meta?.escalatedToHuman ?? thread.escalated_to_human,
        escalation_reason: meta?.escalationReason ?? thread.escalation_reason,
      };
    }),
    [threads, threadBotMeta],
  );
  const notificationSummary = useMemo(
    () => summarizeChatNotifications(
      notificationThreads,
      unreadCount,
      channelDefaultBotId,
    ),
    [notificationThreads, unreadCount, channelDefaultBotId],
  );
  const regularUnreadCount = notificationSummary.regularUnreadMessages;
  const botlessUnreadCount = notificationSummary.botlessUnreadMessages;
  const interventionCount = notificationSummary.humanInterventionThreads;

  // [SYNC-NOTE]: Este cálculo lee directamente de Redux
  const appointmentCount = notificationSummary.appointmentThreads;

  // Filter threads based on platform/text/tags using SSOT intersection (preserve ordering)
  // Robust fallback: if the set is empty but the selected thread qualifies (present in allowedIds), include it
  const filteredThreads = useMemo(() => {
    const base = Array.isArray(threads) ? threads : [];

    // First apply local UI filter based on bot status
    let statusFiltered = base;
    if (botStatusFilter !== 'all') {
      statusFiltered = base.filter(t => {
        const meta = threadBotMeta[t.id];
        const notificationThread: Thread = {
          ...t,
          bot_enabled: meta?.botEnabled ?? t.bot_enabled,
          selected_bot_id: meta?.botId ?? t.selected_bot_id,
          escalated_to_human: meta?.escalatedToHuman ?? t.escalated_to_human,
          escalation_reason: meta?.escalationReason ?? t.escalation_reason,
        };
        if (botStatusFilter === 'human_intervention') {
          return isHumanIntervention(notificationThread);
        } else if (botStatusFilter === 'appointment') {
          return (t as any).appointment_booked === true;
        } else if (botStatusFilter === 'no_bot') {
          return isBotlessThread(notificationThread, channelDefaultBotId);
        } else if (botStatusFilter === 'bot_active') {
          return !isHumanIntervention(notificationThread) &&
            !isBotlessThread(notificationThread, channelDefaultBotId);
        }
        return true;
      });
    }

    if (!allowedIds) return statusFiltered;
    let list = statusFiltered.filter(t => allowedIds.has(t.id));
    if (list.length === 0 && selectedThreadId && allowedIds.has(selectedThreadId)) {
      const st = statusFiltered.find(t => t.id === selectedThreadId);
      if (st) list = [st];
    }
    return list;
  }, [threads, allowedIds, selectedThreadId, botStatusFilter, threadBotMeta, channelDefaultBotId]);

  // [SYNC-NOTE]: Este useEffect es crucial para la sincronización bidireccional.
  // Si un evento SSE (Websockets) actualiza `t.bot_enabled` en Redux, forzamos a que 
  // la caché local `threadBotMeta` adopte el nuevo valor. Sin esto, ChatSidebar ignoraría a Redux.
  // The paginated thread response is the source of truth for per-thread state.
  // Bot names/types are joined from the loaded bot catalog, avoiding one request per row.
  useEffect(() => {
    if (!Array.isArray(threads) || threads.length === 0) return;
    setThreadBotMeta((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const t of threads) {
        const effectiveBotId = t.selected_bot_id ?? activeByChannel.instagram?.id ?? null;
        const effectiveBot = (bots || []).find((bot) => bot.id === effectiveBotId);
        const value = {
          botEnabled: t.bot_enabled ?? true,
          botType: effectiveBot?.bot_type || null,
          botName: effectiveBot?.name || null,
          botId: effectiveBotId,
          escalatedToHuman: Boolean(t.escalated_to_human),
          escalationReason: t.escalation_reason || null,
        };
        const current = next[t.id];
        if (
          !current ||
          current.botEnabled !== value.botEnabled ||
          current.botType !== value.botType ||
          current.botName !== value.botName ||
          current.botId !== value.botId ||
          current.escalatedToHuman !== value.escalatedToHuman ||
          current.escalationReason !== value.escalationReason
        ) {
          next[t.id] = value;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [threads, bots, activeByChannel.instagram]);

  const [commercePlan, setCommercePlan] = useState<string>("base");

  // Per-thread effective bot + override state (channel instagram by default)
  const [effectiveInfo, setEffectiveInfo] = useState<{
    bot_enabled: boolean | null;
    selected_bot_id: number | null;
    selected_bot: any | null;
    default_bot: any | null;
    effective_bot: any | null;
    escalated_to_human: boolean;
    escalation_reason: string | null;
  } | null>(null);
  const [savingOverride, setSavingOverride] = useState<boolean>(false);
  const [showBotMenu, setShowBotMenu] = useState<boolean>(false);
  const botMenuAnchorRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!showBotMenu) return;
    const close = () => setShowBotMenu(false);
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') close(); };
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('resize', close); window.removeEventListener('keydown', onKey); };
  }, [showBotMenu]);
  const [showResolveModal, setShowResolveModal] = useState<boolean>(false);
  const [resolveSelectedBot, setResolveSelectedBot] = useState<string>("");
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const threadsContainerRef = useRef<HTMLDivElement>(null);
  const threadsLoadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const hasScrolledToNewMessagesRef = useRef<string | null>(null);
  const initialScrollThreadRef = useRef<string | null>(null);
  const messageEdgesRef = useRef<{
    threadId: string | null;
    firstId: string | null;
    lastId: string | null;
    length: number;
  } | null>(null);
  const selectedThreadIdRef = useRef<string | null>(selectedThreadId);
  const olderRequestThreadRef = useRef<string | null>(null);
  selectedThreadIdRef.current = selectedThreadId;

  // Load the next bounded page before the user reaches the end of the list. The
  // scroll container is the observer root, so this does not react to page scroll.
  useEffect(() => {
    const root = threadsContainerRef.current;
    const sentinel = threadsLoadMoreSentinelRef.current;
    const searching = Boolean(searchQuery.trim());
    const hasMore = searching ? searchHasMore : threadsHasMore;
    const loading = searching ? searchLoadingMore : threadsLoadingMore;
    if (!isOpen || !root || !sentinel || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void (searching ? loadMoreSearchResults() : loadMoreConversationThreads());
        }
      },
      { root, rootMargin: "300px 0px", threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen, searchQuery, searchHasMore, searchLoadingMore, threadsHasMore, threadsLoadingMore, loadMoreConversationThreads, loadMoreSearchResults]);

  // Auto-resize composer textarea based on content
  useEffect(() => {
    const el = messageInputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const h = Math.min(240, Math.max(40, el.scrollHeight));
    el.style.height = `${h}px`;
  }, [messageText]);

  // Responsive detection
  const [deviceType, setDeviceType] = useState<"mobile" | "tablet" | "desktop">(
    "desktop"
  );

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      if (width < 640) setDeviceType("mobile");
      else if (width < 1024) setDeviceType("tablet");
      else setDeviceType("desktop");
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Track if user is at bottom to avoid jumpy auto-scroll
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const threshold = 40; // px
      isAtBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    };
    handleScroll();
    el.addEventListener("scroll", handleScroll);
    return () => {
      el.removeEventListener("scroll", handleScroll);
    };
  }, [selectedThreadId]);

  const fetchPinnedIds = useCallback(() => {
    try {
      const snap = (chatService as any).getPinnedSnapshot?.() || { pinnedIds: [], pinnedOrder: {} };
      const ids = Array.isArray((snap as any).pinnedIds) ? (snap as any).pinnedIds : [];
      setPinnedIds(new Set(ids.map(String)));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchPinnedIds();
    }
  }, [isOpen, fetchPinnedIds]);

  // Prewarm messages for top visible threads when chat opens or list changes
  useEffect(() => {
    if (isOpen && Array.isArray(threads) && threads.length > 0) {
      try { dispatch(prewarmTopMessages() as any); } catch { }
    }
  }, [isOpen, threads, dispatch]);

  // Subscribe to pinned updates so UI reflects changes instantly without API calls
  useEffect(() => {
    const handler = ({ threadId, isPinned }: any) => {
      setPinnedIds((prev) => {
        const next = new Set(prev);
        const id = String(threadId || '');
        if (!id) return next;
        if (isPinned) next.add(id);
        else next.delete(id);
        return next;
      });
    };
    (chatService as any).on?.('pinned:updated', handler);
    return () => (chatService as any).off?.('pinned:updated', handler);
  }, []);

  // ---- Chat Bots helpers ----
  // Persist and derive comercio_key robustly (fallback to connection-status when localStorage is empty)
  useEffect(() => {
    if (connectionStatus?.comercio_key && typeof window !== "undefined") {
      const cur = localStorage.getItem("comercio_key");
      if (cur !== connectionStatus.comercio_key) {
        localStorage.setItem("comercio_key", connectionStatus.comercio_key);
      }
    }
  }, [connectionStatus?.comercio_key]);

  // Sync tags/folders from RTK Query (instant hydration)
  useEffect(() => {
    setAvailableTags(Array.isArray(tagsWithFolders?.tags) ? (tagsWithFolders!.tags as Tag[]) : []);
    setAvailableFolders(Array.isArray(tagsWithFolders?.folders) ? (tagsWithFolders!.folders as TagFolder[]) : []);
  }, [tagsWithFolders]);

  // Keep selected thread tags in sync (RTK Query per-thread)
  const { data: threadTagsData } = useGetThreadTagsQuery(
    { threadId: selectedThreadId as string },
    { skip: !selectedThreadId }
  );
  useEffect(() => {
    if (!selectedThreadId) {
      setThreadTags([]);
      return;
    }
    setThreadTags(Array.isArray(threadTagsData) ? (threadTagsData as Tag[]) : []);
  }, [selectedThreadId, threadTagsData]);

  // Compute allowed thread ids from platform/text/tags while preserving SSOT order via intersection
  useEffect(() => {
    const compute = async () => {
      const hasText = !!searchQuery.trim();
      const hasTags = selectedTagIds.length > 0;
      const notDefaultPlatform = platformFilter !== 'instagram';

      // No filters active -> reset to null (means show full SSOT list)
      if (!hasText && !hasTags && !notDefaultPlatform) {
        setAllowedIds(null);
        return;
      }

      try {
        // Base ids from current SSOT-ordered list
        const baseIds = Array.isArray(threads) ? threads.map(t => t.id) : [];

        // Platform filter: for now only instagram is supported
        if (notDefaultPlatform) {
          // Selecting a non-instagram platform yields empty result
          setAllowedIds(new Set<string>());
          return;
        }

        // Text filter locally on the visible list
        let textIds: Set<string> | null = null;
        if (hasText) {
          const normalize = (s: any) =>
            String(s || '')
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase();
          const needle = normalize(searchQuery.trim());
          textIds = new Set(
            (threads || [])
              .filter((t: any) => {
                const name = normalize(t.name);
                const username = normalize(t.username || '');
                const lastMessage = normalize(t.lastMessage || '');
                return (
                  name.includes(needle) ||
                  username.includes(needle) ||
                  lastMessage.includes(needle)
                );
              })
              .map((t) => t.id)
          );
        }

        // Tags AND filter using server endpoint over the current scope (intersect with text if present)
        let tagIdsSet: Set<string> | null = null;
        if (hasTags) {
          if (!comercioKey) {
            // Ensure consistent schema resolution: wait until comercio_key is available
            setAllowedIds(new Set<string>());
            return;
          }
          const requiredIds = selectedTagIds.map(Number);
          const scope = textIds ? Array.from(textIds) : baseIds;
          const idsWithAll = await chatService.getThreadIdsWithAllTags(
            requiredIds,
            scope,
            comercioKey || undefined
          );
          tagIdsSet = new Set(idsWithAll);
        }

        // Combine intersections: text ∩ tags (if both present). If only one present, use that. If none, it's already handled.
        let resultSet: Set<string> | null = null;
        if (textIds && tagIdsSet) {
          // intersection
          const res = new Set<string>();
          for (const id of textIds) if (tagIdsSet.has(id)) res.add(id);
          resultSet = res;
        } else if (textIds) {
          resultSet = textIds;
        } else if (tagIdsSet) {
          resultSet = tagIdsSet;
        } else {
          // only non-default platform handled above; fallback to all ids (shouldn't happen here)
          resultSet = new Set(baseIds);
        }

        setAllowedIds(resultSet || new Set<string>());
      } catch {
        setAllowedIds(null);
      }
    };
    void compute();
  }, [threads, searchQuery, selectedTagIds, platformFilter, comercioKey, selectedThreadId, threadTags]);

  const loadAll = useCallback(async () => {
    try {
      await refreshAll();
      if (isDemoModeActive()) { setCommercePlan('premium'); return; }
      try {
        const r = await api.get("/chat/commerce/plan", {
          params: { comercio_key: comercioKey || undefined },
        });
        setCommercePlan(String(r.data?.plan || "base").toLowerCase());
      } catch {
        // ignore commerce plan fetch errors
      }
    } catch (e) {
      console.warn("Error loading bots/channels/plan:", e);
    }
  }, [refreshAll, comercioKey]);

  // Header "force reload" action (hard refresh of lists/panels)
  const handleForceReload = useCallback(async () => {
    try {
      setForceReloading(true);

      // If we are actively viewing a thread, trigger manual sync (bot check) for unverified apps
      if (selectedThreadId && comercioKey) {
        try {
          if (!isDemoModeActive()) await api.post("/v1/instagram/sync", {
            thread_id: selectedThreadId,
            comercio_key: comercioKey
          });
        } catch (syncErr) {
          console.warn("Manual sync error:", syncErr);
        }
      }

      await Promise.all([
        refreshThreads(),
        selectedThreadId ? refreshMessages() : Promise.resolve(),
        loadAll(),
      ]);
    } catch {
      // ignore errors; UI will settle with latest available cache
    } finally {
      // Force reload everything
      dispatch(refreshThreadsThunk());
      // Re-fetching tags here would ideally be a dedicated action or re-trigger
      setForceReloading(false);
    }
  }, [refreshThreads, selectedThreadId, refreshMessages, loadAll, comercioKey, dispatch]);

  // Load per-thread effective info for instagram channel (returns data)
  const fetchEffective = useCallback(async () => {
    try {
      if (!selectedThreadId) {
        setEffectiveInfo(null);
        return null;
      }
      const { data } = await clinicsApi.get(
        `/chat/chat-sessions/${encodeURIComponent(selectedThreadId)}/bot-effective`,
        {
          params: {
            comercio_key: comercioKey || undefined,
            channel: "instagram",
            ts: Date.now(),
          },
        }
      );
      setEffectiveInfo(data || null);
      if (data) {
        setThreadBotMeta((prev) => ({
          ...prev,
          [selectedThreadId]: {
            botEnabled: data.bot_enabled ?? null,
            botType: data.effective_bot?.bot_type || null,
            botName: data.effective_bot?.name || null,
            botId: data.effective_bot?.id ?? data.selected_bot_id ?? data.default_bot?.id ?? null,
            escalatedToHuman: Boolean(data.escalated_to_human),
            escalationReason: data.escalation_reason || null,
          },
        }));
      }
      return data || null;
    } catch (e) {
      console.warn("Error loading effective bot:", e);
      setEffectiveInfo(null);
      return null;
    }
  }, [selectedThreadId, comercioKey]);

  const selectedThreadBotState = useMemo(() => {
    const thread = (threads || []).find(
      (item) => String(item.id) === String(selectedThreadId)
    );
    return `${thread?.bot_enabled ?? "unknown"}:${thread?.selected_bot_id ?? "default"}:${thread?.escalated_to_human ?? false}:${thread?.appointment_booked ?? false}`;
  }, [threads, selectedThreadId]);

  // Refresh selected thread header meta when backend SSE changes handoff/bot
  // state, or when bots/channel defaults change. This keeps the modal and
  // header on the same FastAPI source of truth used by execution.
  useEffect(() => {
    if (selectedThreadId) {
      void fetchEffective();
    }
  }, [bots, activeByChannel, selectedThreadId, selectedThreadBotState, fetchEffective]);

  // Sync the resolve selected bot with the current effective config when opening the modal
  useEffect(() => {
    if (showResolveModal) {
      if (effectiveInfo?.selected_bot_id) {
        setResolveSelectedBot(String(effectiveInfo.selected_bot_id));
      } else {
        setResolveSelectedBot(""); // Default to channel base
      }
    }
  }, [showResolveModal, effectiveInfo]);

  // Helper to persist per-thread override robustly (selected bot and on/off)
  const saveThreadOverride = useCallback(
    async (payload: {
      selected_bot_id?: number | null;
      bot_enabled?: boolean;
      appointment_booked?: boolean;
      escalated_to_human?: boolean;
    }) => {
      if (!selectedThreadId) return;
      try {
        setSavingOverride(true);
        // Call FastAPI instead of Express for SSE real-time updates
        await clinicsApi.put(
          `/chat/chat-sessions/${encodeURIComponent(selectedThreadId)}/bot-override`,
          {
            channel: "instagram",
            ...payload,
          },
          {
            headers: {
              "x-comercio-key": comercioKey || undefined,
            }
          }
        );
        const eff = await fetchEffective();
        if (eff) {
          setThreadBotMeta((prev) => ({
            ...prev,
            [selectedThreadId]: {
              botEnabled: eff.bot_enabled ?? null,
              botType: eff?.effective_bot?.bot_type || null,
              botName: eff?.effective_bot?.name || null,
              botId:
                eff && eff.effective_bot && Number(eff.effective_bot.id)
                  ? Number(eff.effective_bot.id)
                  : null,
              escalatedToHuman: Boolean(eff.escalated_to_human),
              escalationReason: eff.escalation_reason || null,
            },
          }));
        }
        await refreshThreads();
      } catch (err: any) {
        console.error(
          "Error saving thread override:",
          err?.response?.data || err?.message || err
        );
        alert(
          err?.response?.data?.message ||
          err?.message ||
          "Error guardando override del hilo"
        );
      } finally {
        setSavingOverride(false);
      }
    },
    [selectedThreadId, comercioKey, fetchEffective, refreshThreads]
  );

  useEffect(() => {
    if (showSettings) {
      void loadAll();
    }
  }, [showSettings, loadAll]);

  // The header-meta effect above already refreshes effective info when the
  // selected thread changes. Only ensure the override selector has its bot list
  // here; fetching effective info again caused duplicate GET requests.
  useEffect(() => {
    if (bots.length === 0) {
      void loadAll();
    }
  }, [selectedThreadId, bots.length, loadAll]);

  // Activate mapping per-channel
  const activateForChannel = useCallback(
    async (botId: number, channel: "instagram" | "whatsapp" | "email") => {
      try {
        await activateForChannelCtx({ id: botId, channel }).unwrap();
      } catch (e: any) {
        alert(
          e?.response?.data?.message ||
          e?.message ||
          "Error al activar el bot por canal"
        );
      }
    },
    [activateForChannelCtx]
  );


  const goToBotConfig = useCallback((id: number) => {
    try {
      window.location.href = `/bots-config?botId=${id}`;
    } catch {
      window.location.href = "/bots-config";
    }
  }, []);

  // In-chat Instagram connect (same flow as ConnectionsPage.handleConnectInstagram)
  const handleConnectInstagram = useCallback(async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token || token === 'null' || token.trim() === '') {
      alert('Error de autenticación. No se encontró un token válido. Por favor, inicia sesión.');
      try { window.location.href = '/login'; } catch { }
      return;
    }
    try {
      const { data } = await fastapi.get<{ authorization_url: string }>("/auth/instagram/connect-url");
      if (!data.authorization_url) throw new Error("Missing Instagram authorization URL");
      window.location.assign(data.authorization_url);
    } catch (error: any) {
      alert(error?.response?.data?.detail || "No se pudo iniciar la conexión con Instagram.");
    }
  }, []);

  const handleTogglePin = async (threadId: string) => {
    const isCurrentlyPinned = pinnedIds.has(threadId);
    // The useChat hook performs the API call; SSE + optimistic cache will update UI
    await togglePin(threadId, isCurrentlyPinned);
  };

  // Default AI selected bot: effective -> selected override -> channel default
  useEffect(() => {
    const defId =
      (effectiveInfo?.effective_bot as any)?.id ??
      effectiveInfo?.selected_bot_id ??
      (activeByChannel.instagram as any)?.id ??
      null;
    setAISelectedBotId(defId ?? "");
  }, [
    effectiveInfo?.effective_bot,
    effectiveInfo?.selected_bot_id,
    activeByChannel.instagram,
    selectedThreadId,
  ]);

  const fetchAIDrafts = useCallback(async () => {
    if (!selectedThreadId) return;
    try {
      setAIDraftsLoading(true);
      setAIError(null);
      const resp = await chatService.draftBotResponse(selectedThreadId, {
        botId: aiSelectedBotId === "" ? undefined : Number(aiSelectedBotId),
        channel: "instagram",
        historyLimit: 20,
      });
      const list = Array.isArray(resp?.suggestions)
        ? resp.suggestions.filter((s: any) => typeof s === "string")
        : [];
      setAISuggestions(list.length ? list : [""]);
      setAIUsedBotName(
        resp && resp.used_bot && resp.used_bot.name
          ? String(resp.used_bot.name)
          : null
      );
    } catch (e: any) {
      setAIError(
        e?.response?.data?.message ||
        e?.message ||
        "Error obteniendo sugerencias"
      );
    } finally {
      setAIDraftsLoading(false);
    }
  }, [selectedThreadId, aiSelectedBotId]);

  // Reset hasScrolledToNewMessagesRef when thread changes
  useEffect(() => {
    hasScrolledToNewMessagesRef.current = null;
    initialScrollThreadRef.current = null;
    messageEdgesRef.current = null;
    olderRequestThreadRef.current = null;
  }, [selectedThreadId]);

  // Auto-scroll to bottom when messages change (only if user is at bottom or it's my own message), or to new messages
  useEffect(() => {
    const first = Array.isArray(messages) ? messages[0] : null;
    const last = Array.isArray(messages) ? messages[messages.length - 1] : null;
    const previousEdges = messageEdgesRef.current;
    const prependedOlderHistory =
      previousEdges?.threadId === selectedThreadId &&
      previousEdges.lastId !== null &&
      previousEdges.lastId === (last?.id ?? null) &&
      previousEdges.firstId !== (first?.id ?? null) &&
      messages.length > previousEdges.length;

    messageEdgesRef.current = {
      threadId: selectedThreadId,
      firstId: first?.id ?? null,
      lastId: last?.id ?? null,
      length: messages.length,
    };

    const needsInitialPosition =
      !!selectedThreadId && initialScrollThreadRef.current !== selectedThreadId;

    if (firstNewMessageId && hasScrolledToNewMessagesRef.current !== selectedThreadId) {
      const newMessagesMarker = document.getElementById("new-messages-marker");
      if (newMessagesMarker) {
        newMessagesMarker.scrollIntoView({ behavior: "auto", block: "center" });
        hasScrolledToNewMessagesRef.current = selectedThreadId;
        initialScrollThreadRef.current = selectedThreadId;
        return;
      }
    }

    if (needsInitialPosition && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      initialScrollThreadRef.current = selectedThreadId;
      return;
    }

    // Loading an older page changes the first message, not the last one. The
    // caller restores the viewport anchor after the prepend, so do not let the
    // existing outgoing-message auto-scroll compete with that restoration.
    if (prependedOlderHistory) return;

    // Only auto scroll to bottom if the last message is from the user AND there are no unread messages.
    // If it's a bot message (origin != web and senderKind != human), we don't force scroll to bottom
    // if the thread has unread messages, so the user can see them.
    const thread = Array.isArray(threads) ? threads.find(t => t.id === selectedThreadId) : null;
    const hasUnread = thread?.unread || (thread as any)?.unreadClientCount > 0 || (thread as any)?.unreadBotCount > 0;

    // Determine if the last message is genuinely from the current web user (not a bot/automation)
    const isHumanWeb = last?.fromMe && (last.origin === 'web' || last.senderKind === 'human');

    if (last?.fromMe && (isHumanWeb || !hasUnread)) {
      messagesEndRef.current?.scrollIntoView({
        behavior: "auto",
      });
    }
  }, [messages, firstNewMessageId, selectedThreadId, threads]);

  // Handle window focus and visibility changes to mark messages as read only when visible at bottom
  useEffect(() => {
    if (isOpen && selectedThreadId && document.hasFocus()) {
      // Re-select the thread to reset the new message marker (only if needed)
      // Note: We NO LONGER auto-mark as read here. markAsRead is dispatched when scrolling to bottom.
    }
  }, [isOpen, selectedThreadId, selectThread]);

  // Handle auto mark-as-read when the user is at the bottom of the conversation
  useEffect(() => {
    const checkReadState = () => {
      const container = messagesContainerRef.current;
      if (!container || !selectedThreadId || !isOpen || !document.hasFocus()) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= 5;

      if (isAtBottom) {
        const thread = filteredThreads.find(t => t.id === selectedThreadId);
        if (thread?.unread || (thread as any)?.unreadClientCount > 0) {
          dispatch(markAsRead(selectedThreadId) as any);
        }
      }
    };
    checkReadState();
  }, [messages, selectedThreadId, filteredThreads, isOpen, dispatch]);

  // Selected thread info
  const selectedThread = threads.find((t) => t.id === selectedThreadId);
  const selectedBotMeta = selectedThread ? threadBotMeta[selectedThread.id] : undefined;
  const selectedNotificationThread: Thread | null = selectedThread ? {
    ...selectedThread,
    bot_enabled: selectedBotMeta?.botEnabled ?? selectedThread.bot_enabled,
    selected_bot_id: selectedBotMeta?.botId ?? selectedThread.selected_bot_id,
    escalated_to_human: selectedBotMeta?.escalatedToHuman ?? selectedThread.escalated_to_human,
    escalation_reason: selectedBotMeta?.escalationReason ?? selectedThread.escalation_reason,
  } : null;
  const selectedIsHumanIntervention = selectedNotificationThread
    ? isHumanIntervention(selectedNotificationThread)
    : false;
  const selectedIsBotless = selectedNotificationThread
    ? isBotlessThread(selectedNotificationThread, channelDefaultBotId)
    : false;
  const selectedReplyWindowEndsAt = useMemo(() => {
    const threadDeadline = resolveThreadReplyWindowEndsAt(selectedThread);
    if (switchingThread) return threadDeadline;

    const latestInboundAt = (Array.isArray(messages) ? messages : []).reduce<string | null>(
      (latest, message) => {
        if (message.fromMe) return latest;
        const messageMs = Date.parse(message.createdAt || '');
        const latestMs = Date.parse(latest || '');
        if (!Number.isFinite(messageMs)) return latest;
        return !Number.isFinite(latestMs) || messageMs > latestMs
          ? message.createdAt
          : latest;
      },
      null,
    );
    const messageDeadline = replyWindowEndsAtFromInbound(latestInboundAt);
    if (!threadDeadline) return messageDeadline;
    if (!messageDeadline) return threadDeadline;
    return Date.parse(messageDeadline) > Date.parse(threadDeadline)
      ? messageDeadline
      : threadDeadline;
  }, [selectedThread, messages, switchingThread]);
  const selectedReplyWindowBlocked = useReplyWindowBlocked(
    selectedReplyWindowEndsAt,
    selectedThread?.platform,
  );

  // Handle send message
  const handleSendMessage = useCallback(async () => {
    if (!messageText.trim() || sendingMessage || selectedReplyWindowBlocked) return;

    try {
      await sendMessage(messageText.trim(), undefined, {
        senderKind,
        origin,
        botName: senderKind === "bot" ? botName || "generic" : undefined,
      });
      setMessageText("");
      messageInputRef.current?.focus();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  }, [messageText, sendingMessage, selectedReplyWindowBlocked, sendMessage, senderKind, botName]);

  const handleGenerateDemoRole = useCallback(async (role: 'customer' | 'operator') => {
    if (!selectedThreadId || demoGeneratingRole) return;
    setDemoGeneratingRole(role);
    setAIError(null);
    try {
      const generated = await chatService.generateDemoAIMessage(selectedThreadId, role, role === 'customer');
      if (role === 'operator') {
        setSenderKind('human');
        setMessageText(generated);
        messageInputRef.current?.focus();
      }
    } catch (error: any) {
      setAIError(error?.response?.data?.detail || error?.message || 'No se pudo generar el mensaje con IA.');
      setShowAIDrafts(true);
    } finally {
      setDemoGeneratingRole(null);
    }
  }, [selectedThreadId, demoGeneratingRole]);

  const handleSendImage = useCallback(() => {
    if (!selectedReplyWindowBlocked) { setMediaOpen(true); }
  }, [selectedReplyWindowBlocked]);

  // Handle key press in message input
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const loadOlderPage = useCallback((container: HTMLDivElement) => {
    const requestThreadId = selectedThreadId;
    if (
      !requestThreadId ||
      !nextMessagesCursor ||
      messagesLoading ||
      loadingOlder ||
      olderRequestThreadRef.current === requestThreadId
    ) {
      return;
    }

    olderRequestThreadRef.current = requestThreadId;
    const previousScrollHeight = container.scrollHeight;
    const previousScrollTop = container.scrollTop;

    void dispatch(loadOlderMessages())
      .unwrap()
      .then(() => {
        requestAnimationFrame(() => {
          if (selectedThreadIdRef.current !== requestThreadId) return;
          const activeContainer = messagesContainerRef.current;
          if (!activeContainer || activeContainer !== container) return;
          const addedHeight = activeContainer.scrollHeight - previousScrollHeight;
          activeContainer.scrollTop = previousScrollTop + Math.max(0, addedHeight);
        });
      })
      .catch(() => {
        // The existing message list stays usable when an older-page request fails.
      })
      .finally(() => {
        if (olderRequestThreadRef.current === requestThreadId) {
          olderRequestThreadRef.current = null;
        }
      });
  }, [dispatch, loadingOlder, messagesLoading, nextMessagesCursor, selectedThreadId]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop <= 50) {
      loadOlderPage(e.currentTarget);
    }

    // Check if at bottom to trigger markAsRead
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    if (isAtBottom && selectedThreadId && isOpen && document.hasFocus()) {
      const thread = filteredThreads.find(t => t.id === selectedThreadId);
      if (thread?.unread || (thread as any)?.unreadClientCount > 0) {
        dispatch(markAsRead(selectedThreadId) as any);
      }
    }
  };

  // Ten short messages may not fill the panel. Keep loading bounded pages until
  // there is enough content to scroll, while preserving the visible anchor.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !nextMessagesCursor || messagesLoading || loadingOlder) return;
    if (container.scrollHeight <= container.clientHeight + 1) {
      loadOlderPage(container);
    }
  }, [loadOlderPage, loadingOlder, messages.length, messagesLoading, nextMessagesCursor]);

  // Helper to format date
  const formatDatePill = (isoString: string) => {
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return t('chatSidebar.date.today', 'Hoy');
    if (date.toDateString() === yesterday.toDateString()) return t('chatSidebar.date.yesterday', 'Ayer');
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Group messages by sender for avatar display (defensive against null/undefined)
  const safeMessages = Array.isArray(messages) ? messages : [];
  const groupedMessages = safeMessages.reduce(
    (acc, msg, index) => {
      const prevMsg = safeMessages[index - 1];
      const nextMsg = safeMessages[index + 1];

      // Date Separator logic
      let dateSeparator = null;
      if (!prevMsg) {
        dateSeparator = msg.createdAt;
      } else {
        const prevDate = new Date(prevMsg.createdAt).toDateString();
        const currDate = new Date(msg.createdAt).toDateString();
        if (prevDate !== currDate) {
          dateSeparator = msg.createdAt;
        }
      }

      if (dateSeparator) {
        acc.push({ isDateSeparator: true, id: `date-${dateSeparator}`, dateString: formatDatePill(dateSeparator) } as any);
      }

      const showAvatar = !prevMsg || prevMsg.fromMe !== msg.fromMe || !!dateSeparator;
      const isLastFromSender = !nextMsg || nextMsg.fromMe !== msg.fromMe || new Date(nextMsg.createdAt).toDateString() !== new Date(msg.createdAt).toDateString();

      acc.push({ ...msg, showAvatar, isLastFromSender });
      return acc;
    },
    [] as Array<Message & { showAvatar: boolean; isLastFromSender: boolean; isDateSeparator?: boolean; dateString?: string }>
  );

  if (!isOpen) {
    // Floating button when closed
    return (
      <div data-ui="chat-sidebar-launcher" data-theme={theme} className="chat-sidebar-theme fixed bottom-6 right-6 z-40">
        <BtnTaratic
          data-ui="chat-sidebar-launcher-button"
          onClick={openChat}
          color="blue"
          className="btn-blue w-14 h-14 !rounded-full flex items-center justify-center !p-0 shadow-inner-glow shadow-inner-glow-white-2xl shadow-xl"
          aria-label={t('chatSidebar.title')}
        >
          <svg
            className="w-6 h-6 icon-inner-glow-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </BtnTaratic>
        {regularUnreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center border border-black/20 z-10 pointer-events-none shadow-sm">
            {regularUnreadCount > 9 ? "9+" : regularUnreadCount}
          </span>
        )}
        {botlessUnreadCount > 0 && (
          <span
            className="absolute -bottom-1 -right-1 bg-orange-500 text-white text-xs rounded-full min-w-5 h-5 px-1 flex items-center justify-center border border-black/20 z-10 pointer-events-none shadow-[0_0_10px_rgba(249,115,22,0.6)]"
            title="Mensajes sin bot disponibles"
          >
            {botlessUnreadCount > 9 ? "9+" : botlessUnreadCount}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      data-ui="chat-sidebar-layer"
      data-theme={theme}
      className={`
      chat-sidebar-theme fixed inset-0 flex overflow-x-hidden
      ${isDocked ? "z-30" : "z-50"}
      ${deviceType === "mobile" ? "" : "lg:inset-auto lg:right-0 lg:top-0 lg:h-screen lg:w-[900px]"}
    `}
    >
      {/* Backdrop for mobile/tablet or non-docked overlay */}
      {(deviceType !== "desktop" || !isDocked) && (
        <div
          data-ui="chat-sidebar-backdrop"
          className="absolute inset-0 bg-black/30 lg:hidden"
          onClick={closeChat}
        />
      )}

      {/* Main container */}
      <div
        data-ui="chat-sidebar-panel"
        className={`
        relative bg-black/50 backdrop-blur-xl shadow-2xl flex flex-col min-h-0 max-w-full overflow-x-hidden
        ${deviceType === "mobile"
            ? "w-full h-full"
            : deviceType === "tablet"
              ? "w-full h-full lg:w-[400px]"
              : "w-full h-full"
          }
      `}
      >
        {/* Header */}
        <div data-ui="chat-sidebar-header" className="flex items-center justify-between p-4 border-b border-white/10 bg-black/75">
          <div data-ui="chat-sidebar-title-and-status" className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">{t('chatSidebar.title')}</h2>
            {connectionStatus?.ig_username && (
              <>
                <span className="text-sm text-white/50">
                  @{connectionStatus.ig_username}
                </span>
                {regularUnreadCount > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                    {regularUnreadCount > 99 ? "99+" : regularUnreadCount}
                  </span>
                )}
                {botlessUnreadCount > 0 && (
                  <span
                    className="ml-1 bg-orange-500 text-white text-xs font-bold rounded-full px-2 py-0.5 shadow-[0_0_8px_rgba(249,115,22,0.6)]"
                    title="Mensajes sin bot disponibles"
                  >
                    {botlessUnreadCount > 99 ? "99+" : botlessUnreadCount}
                  </span>
                )}
                {interventionCount > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center gap-1 min-w-[20px] h-5 px-1.5 rounded-full bg-yellow-500 text-black text-[10px] font-bold shadow-[0_0_8px_rgba(234,179,8,0.6)] animate-pulse" title="Intervención Humana">
                    <FontAwesomeIcon icon={faUser} className="w-2.5 h-2.5" />
                    {interventionCount > 99 ? '99+' : interventionCount}
                  </span>
                )}
              </>
            )}
            {!isConnected && (
              <span className="text-xs text-red-500">{t('chatSidebar.disconnected')}</span>
            )}
          </div>
          <div data-ui="chat-sidebar-header-actions" className="flex items-center gap-2">
            <BtnTaratic
              onClick={handleForceReload}
              disabled={forceReloading || threadsLoading}
              color="ghost-dark"
              iconOnly
              className="transition-all disabled:opacity-60"
              title={t('chatSidebar.buttons.refresh')}
              aria-label={t('chatSidebar.buttons.refresh')}
            >
              <i className={`fa-solid fa-arrows-rotate ${(forceReloading || threadsLoading) ? 'fa-spin' : ''} text-[18px]`}></i>
            </BtnTaratic>
            <BtnTaratic
              onClick={() => setShowSettings(!showSettings)}
              color={showSettings ? "blue" : "ghost-dark"}
              iconOnly
              className="transition-all"
              title={t('chatSidebar.settings')}
            >
              <i className="fa-solid fa-gear text-[18px]"></i>
            </BtnTaratic>
            <BtnTaratic
              onClick={closeChat}
              color="ghost-dark"
              iconOnly
              className="transition-all"
              title={t('chatSidebar.close')}
            >
              <i className="fa-solid fa-times text-[18px]"></i>
            </BtnTaratic>
          </div>
        </div>

        {/* Settings panel */}
        {showSettings && (
          <div data-ui="chat-sidebar-settings-panel" className="p-4 bg-white/20/70 border-b space-y-4">
            {/* Preferences */}
            <div data-ui="chat-sidebar-preferences" className="flex flex-col gap-3">
              <div data-ui="chat-sidebar-region-037" className="flex items-center justify-between">
                <GhostSwitch
                  variant={theme}
                  checked={soundEnabled}
                  onChange={(val) => setSoundEnabled(val)}
                  label={<span className="text-sm">{t('chatSidebar.settingsPanel.soundNotifications')}</span>}
                />

                {/* Quick access to full bot configuration page */}
                <BtnTaratic
                  onClick={() => (window.location.href = "/bots-config")}
                  color="ghost-dark"
                  size="xs"
                  className="text-[11px] py-0.5 px-2"
                  title={t('chatSidebar.settingsPanel.openBotsConfigTitle')}
                >
                  {t('chatSidebar.settingsPanel.openBotsConfig')}
                </BtnTaratic>
              </div>

              {/* Dock Chat Toggle */}
              <div data-ui="chat-sidebar-region-038" className="flex items-center justify-between">
                <GhostSwitch
                  variant={theme}
                  checked={isDocked}
                  onChange={(val) => setDocked(val)}
                  label={<span className="text-sm">{t('chatSidebar.settingsPanel.dockChat')}</span>}
                />
              </div>
            </div>

            {/* JWT status */}
            <div data-ui="chat-sidebar-session-status" className="flex items-center justify-between">
              <div data-ui="chat-sidebar-region-039" className="text-xs">
                <span
                  className={`px-2 py-1 rounded-full border ${jwtExpired
                    ? "bg-red-100 border-red-300 text-red-700"
                    : jwtSecondsLeft !== null && jwtSecondsLeft <= 300
                      ? "bg-amber-100 border-amber-300 text-amber-800"
                      : "bg-white/10 border-white/20 text-white/70"
                    }`}
                  title={jwtExpired ? t('chatSidebar.jwt.expired') : t('chatSidebar.jwt.tokenExpiresInTitle', { time: formatPrettyLeft(jwtSecondsLeft) })}
                  aria-live="polite"
                >
                  {jwtExpired ? t('chatSidebar.jwt.expired') : t('chatSidebar.jwt.expiresIn', { time: formatPrettyLeft(jwtSecondsLeft) })}
                </span>
              </div>
              <BtnTaratic
                onClick={async () => {
                  try { await dispatch(logoutThunk()).unwrap(); } catch { }
                  try { window.location.href = '/login'; } catch { }
                }}
                color={jwtExpired ? "red" : "ghost-dark"}
                size="xs"
                className={`text-[11px] py-0.5 px-2 ${jwtExpired ? "" : "text-amber-300 border border-amber-300/40"}`}
                title={t('chatSidebar.jwt.reauth')}
              >
                {t('chatSidebar.jwt.reauth')}
              </BtnTaratic>
            </div>

            {/* Plan info */}
            <div data-ui="chat-sidebar-region-040" className="text-xs text-white/60">
              {t('chatSidebar.settingsPanel.commercePlanLabel')}{" "}
              <span className="font-medium">{commercePlan}</span>
            </div>

            {/* Per-thread (instagram) effective bot summary and override */}
            {selectedThreadId && (
              <div data-ui="chat-sidebar-thread-bot-settings" className="p-3 bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-gray-500-md border border-white">
                <div data-ui="chat-sidebar-region-041" className="flex items-center justify-between">
                  <div data-ui="chat-sidebar-region-042" className="text-sm font-semibold text-white/70">
                    {t('chatSidebar.bot.threadMenuTitle')}
                  </div>
                  <BtnTaratic
                    onClick={() => fetchEffective()}
                    color="ghost-dark"
                    size="xs"
                    className="btn-compact-sm"
                    title={t('chatSidebar.buttons.refresh')}
                  >
                    {t('chatSidebar.buttons.refresh')}
                  </BtnTaratic>
                </div>
                <div data-ui="chat-sidebar-region-043" className="mt-2 grid grid-cols-1 lg:grid-cols-3 gap-1.5 text-[10px]">
                  <div data-ui="chat-sidebar-region-044">
                    <div data-ui="chat-sidebar-region-045" className="text-white font-bold uppercase tracking-wider">{t('chatSidebar.bot.effective.label')}</div>
                    <div data-ui="chat-sidebar-region-046" className="text-white/80 flex items-center gap-1.5 mt-0.5 leading-tight">
                      <span>
                        {effectiveInfo?.bot_enabled === false
                          ? t('chatSidebar.bot.offNoBot')
                          : effectiveInfo?.effective_bot
                            ? `${effectiveInfo?.effective_bot?.name} · ${effectiveInfo?.effective_bot?.bot_type || "general"} · ${effectiveInfo?.effective_bot?.pricing_type || "base"}`
                            : "—"}
                      </span>
                      {effectiveInfo?.effective_bot && (
                        <BtnTaratic
                          onClick={() =>
                            goToBotConfig(effectiveInfo.effective_bot.id)
                          }
                          color="ghost-dark"
                          size="xs"
                          className="!py-0.5 !px-1.5 text-[9px] h-auto min-h-0"
                          title={t('chatSidebar.bot.effective.configureTitle')}
                        >
                          {t('chatSidebar.buttons.configure')}
                        </BtnTaratic>
                      )}
                    </div>
                  </div>
                  <div data-ui="chat-sidebar-region-047">
                    <div data-ui="chat-sidebar-region-048" className="text-white font-bold uppercase tracking-wider">{t('chatSidebar.bot.override.label')}</div>
                    <div data-ui="chat-sidebar-region-049" className="text-white/80 mt-0.5 leading-tight text-[11px]">
                      {effectiveInfo?.selected_bot
                        ? `${effectiveInfo?.selected_bot?.name} · ${effectiveInfo?.selected_bot?.bot_type || "general"} · ${effectiveInfo?.selected_bot?.pricing_type || "base"}`
                        : t('chatSidebar.bot.override.noneOption')}
                    </div>
                  </div>
                  <div data-ui="chat-sidebar-region-050">
                    <div data-ui="chat-sidebar-region-051" className="text-white font-bold uppercase tracking-wider">{t('chatSidebar.bot.channelDefault.label')}</div>
                    <div data-ui="chat-sidebar-region-052" className="text-white/80 mt-0.5 leading-tight text-[11px]">
                      {activeByChannel.instagram
                        ? `${activeByChannel.instagram.name} · ${activeByChannel.instagram.bot_type || "general"} · ${activeByChannel.instagram.pricing_type || "base"}`
                        : t('chatSidebar.common.notDefined')}
                    </div>
                  </div>
                </div>

                {/* Override controls */}
                <div data-ui="chat-sidebar-region-053" className="mt-2 flex flex-wrap gap-x-4 gap-y-2 items-center">
                  <div data-ui="chat-sidebar-region-054" className="flex items-center gap-2">
                    <span className="text-[10px] text-white font-bold uppercase tracking-wider">{t('chatSidebar.bot.override.label')}</span>
                    <GhostSelect
                      size="sm"
                      className="min-w-[150px]"
                      disabled={botsLoading || savingOverride}
                      value={effectiveInfo?.selected_bot_id ? String(effectiveInfo?.selected_bot_id) : ""}
                      onChange={async (val: string) => {
                        const id = val ? Number(val) : NaN;
                        await saveThreadOverride({
                          selected_bot_id: Number.isFinite(id) ? id : null,
                        });
                      }}
                      options={[
                        { value: "", label: t('chatSidebar.bot.override.noneOption') || "Usar Bot de Sistema" },
                        ...(bots || [])
                          .filter((b) => !b.channels || b.channels.includes("instagram"))
                          .map((b) => ({
                            value: String(b.id),
                            label: `${b.name} (tipo: ${b.bot_type || "general"})`
                          }))
                      ]}
                    />
                  </div>
                  <div data-ui="chat-sidebar-region-055" className="flex items-center gap-2">
                    <span className="text-[10px] text-white/60 uppercase tracking-wider">
                      {t('chatSidebar.bot.enabledThreadLabel')}
                    </span>
                    <GhostSwitch
                      variant={theme}
                      size="sm"
                      disabled={savingOverride}
                      checked={!!(effectiveInfo?.bot_enabled ?? true)}
                      onChange={async (val) => {
                        await saveThreadOverride({
                          bot_enabled: val,
                        });
                      }}
                    />
                    <span className="text-[10px] font-semibold tracking-wide text-white/80">
                      {(effectiveInfo?.bot_enabled ?? true) ? 'ON' : 'OFF'}
                    </span>
                  </div>
                  {isDemoModeActive() && (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-white/60 uppercase tracking-wider">{t('chatSidebar.thread.humanIntervention')}</span>
                        <GhostSwitch
                          variant={theme}
                          size="sm"
                          disabled={savingOverride}
                          checked={selectedIsHumanIntervention}
                          onChange={(val) => saveThreadOverride({ escalated_to_human: val })}
                          ariaLabel={t('chatSidebar.thread.humanIntervention')}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-white/60 uppercase tracking-wider">{t('chatSidebar.thread.appointmentScheduledByAI')}</span>
                        <GhostSwitch
                          variant={theme}
                          size="sm"
                          disabled={savingOverride}
                          checked={!!selectedThread?.appointment_booked}
                          onChange={(val) => saveThreadOverride({ appointment_booked: val })}
                          ariaLabel={t('chatSidebar.thread.appointmentScheduledByAI')}
                        />
                      </div>
                    </>
                  )}
                  <div data-ui="chat-sidebar-region-056" className="text-right flex-1">
                    {savingOverride && (
                      <span className="text-[10px] text-white/50">
                        {t('chatSidebar.common.saving')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Active bot per channel selector */}
            <div data-ui="chat-sidebar-channel-bot-settings" className="space-y-2.5">
              <div data-ui="chat-sidebar-region-057" className="flex items-center gap-2 flex-wrap justify-between">
                <h3 className="text-sm font-semibold text-white/80">
                  {t('chatSidebar.channels.selectBotByChannel')}
                </h3>
                <BtnTaratic
                  onClick={() => loadAll()}
                  color="ghost-dark"
                  size="xs"
                  className="btn-compact-sm"
                  title={t('chatSidebar.buttons.refresh')}
                >
                  {t('chatSidebar.buttons.refresh')}
                </BtnTaratic>
              </div>

              {/* Channel rows */}
              {(
                ["instagram", "whatsapp", "email"] as Array<
                  "instagram" | "whatsapp" | "email"
                >
              ).map((ch) => {
                const connected = channelsStatus[ch];
                const active = activeByChannel[ch];
                const candidates = bots.filter(
                  (b) => !b.channels || b.channels.includes(ch)
                );
                return (
                  <div
                    data-ui={`chat-sidebar-channel-${ch}`}
                    key={ch}
                    className="flex items-center gap-3 p-3 bg-black/20 backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-gray-500-sm"
                  >
                    <div data-ui="chat-sidebar-region-058" className="w-28 text-xs uppercase text-white/70 font-medium flex items-center gap-2">
                      <span aria-hidden="true">
                        {ch === "instagram"
                          ? <i className="fa-brands fa-instagram text-pink-400" aria-hidden="true"></i>
                          : ch === "whatsapp"
                            ? <i className="fa-brands fa-whatsapp text-emerald-400" aria-hidden="true"></i>
                            : <i className="fa-solid fa-envelope text-blue-400" aria-hidden="true"></i>}
                      </span>
                      <span>{ch === "instagram" ? t('chatSidebar.instagram') : ch === "whatsapp" ? t('chatSidebar.whatsapp') : t('chatSidebar.email')}</span>
                    </div>
                    <div data-ui="chat-sidebar-region-059" className="flex-1">
                      <GhostSelect
                        disabled={!connected || botsLoading}
                        className={`w-full ${!connected ? "opacity-50" : ""}`}
                        value={active?.id ? String(active?.id) : ""}
                        onChange={(val: string) => {
                          const raw = val;
                          const channel = ch as Channel;
                          if (!raw) {
                            // Dejar el canal sin bot por defecto
                            void clearActiveForChannelCtx({ channel }).unwrap();
                            return;
                          }
                          const id = Number(raw);
                          if (Number.isFinite(id) && id > 0) {
                            void activateForChannel(id, channel);
                          }
                        }}
                        options={[
                          { value: "", label: t('chatSidebar.channels.noDefaultOption') || "Sin bot por defecto (desactivado)" },
                          ...candidates.map((b) => ({
                            value: String(b.id),
                            label: `${b.name} (${b.bot_type || "general"})`
                          }))
                        ]}
                      />
                    </div>
                    {active && (
                      <>
                        <div data-ui="chat-sidebar-region-060" className="hidden lg:block text-xs text-white/80">
                          <div data-ui="chat-sidebar-region-061">
                            <span className="text-white font-bold">{t('chatSidebar.channels.role')}</span>{" "}
                            {active.bot_type || "general"}
                          </div>
                        </div>
                        <BtnTaratic
                          type="button"
                          color="ghost-dark"
                          size="xs"
                          className="btn-compact-xs"
                          onClick={() => goToBotConfig(active.id)}
                          title={t('chatSidebar.channels.configureDefaultBotTitle')}
                        >
                          {t('chatSidebar.buttons.configure')}
                        </BtnTaratic>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            <div data-ui="chat-sidebar-channel-help" className="mt-3 p-3 rounded-xl bg-blue-600/15 border border-blue-500/30 text-blue-200 text-xs flex items-start gap-2.5 shadow-sm">
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-400 w-4 h-4 shrink-0 mt-0.5" />
              <p className="leading-relaxed">
                {t('chatSidebar.channels.note')}
              </p>
            </div>
          </div>
        )}

        {/* Content area */}
        <div data-ui="chat-sidebar-content" className="relative flex-1 flex overflow-hidden min-h-0">
          {/* Threads list */}
          {(deviceType === "desktop" ||
            !selectedThreadId ||
            showMobileThreads) && (
              <div
                data-ui="chat-sidebar-thread-list"
                ref={threadsContainerRef}
                className={`
              bg-black/75 border-r border-white/10 overflow-y-auto overflow-x-hidden
              ${deviceType === "mobile" && selectedThreadId
                    ? "absolute inset-0 z-10"
                    : deviceType === "tablet" && selectedThreadId
                      ? "absolute inset-0 z-10"
                      : "w-full lg:w-[400px]"
                  }
            `}
              >
                {/* Search + Filter */}
                <div data-ui="chat-sidebar-thread-search-and-filters" className="p-3 border-b border-white/10 relative z-30">
                  <div data-ui="chat-sidebar-search-row" className="flex items-center gap-2">
                    <div data-ui="chat-sidebar-search-field" className="relative flex-1">
                      <input
                        type="text"
                        placeholder={t('chatSidebar.searchConversations')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-white/10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <svg
                        className="absolute left-3 top-2.5 w-4 h-4 text-white/40"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <BtnTaratic
                      type="button"
                      onClick={() => setShowFilter((v) => !v)}
                      color={showFilter ? "blue" : "ghost-dark"}
                      size="sm"
                      className="flex items-center gap-2 rounded-full text-sm transition-all duration-300"
                      title={t('chatSidebar.filters')}
                    >
                      <i className="fa fa-filter" aria-hidden="true"></i>
                      <span>{t('chatSidebar.filters')}</span>
                    </BtnTaratic>
                  </div>

                  {/* Bot Status Sliding Pills - Centered & Right below search & filters */}
                  <div data-ui="chat-sidebar-status-filter" className="mt-3 flex justify-center w-full relative z-40">
                    <SlidingPills
                      options={[
                        { id: 'all', label: t('chatSidebar.thread.filterAll'), icon: faList },
                        { id: 'bot_active', label: t('chatSidebar.thread.filterBotActive'), icon: faRobot },
                        {
                          id: 'human_intervention',
                          label: t('chatSidebar.thread.filterHumanIntervention'),
                          customIcon: (
                            <div data-ui="chat-sidebar-region-062" className="relative inline-flex">
                              <FontAwesomeIcon icon={faUser} />
                              {interventionCount > 0 && (
                                <span className="absolute -top-2 -right-2.5 min-w-[15px] h-3.5 flex items-center justify-center rounded-full bg-yellow-500 text-black text-[9px] font-bold px-1 border border-black shadow-[0_0_8px_rgba(234,179,8,0.6)] animate-pulse">
                                  {interventionCount}
                                </span>
                              )}
                            </div>
                          )
                        },
                        {
                          id: 'appointment',
                          label: t('chatSidebar.thread.filterNewAppointments'),
                          customIcon: (
                            <div data-ui="chat-sidebar-region-063" className="relative inline-flex">
                              <FontAwesomeIcon icon={faCalendarCheck} />
                              {appointmentCount > 0 && (
                                <span className="absolute -top-2 -right-2.5 min-w-[15px] h-3.5 flex items-center justify-center rounded-full bg-emerald-500 text-black text-[9px] font-bold px-1 border border-black shadow-[0_0_8px_rgba(16,185,129,0.6)] animate-pulse">
                                  {appointmentCount}
                                </span>
                              )}
                            </div>
                          )
                        },
                        {
                          id: 'no_bot',
                          label: t('chatSidebar.thread.filterNoBot'),
                          customIcon: (
                            <div data-ui="chat-sidebar-region-064" className="relative inline-flex">
                              <FontAwesomeIcon icon={faPowerOff} />
                              {botlessUnreadCount > 0 && (
                                <span className="absolute -top-2 -right-2.5 min-w-[15px] h-3.5 flex items-center justify-center rounded-full bg-orange-500 text-white text-[9px] font-bold px-1 border border-black shadow-[0_0_8px_rgba(249,115,22,0.6)]">
                                  {botlessUnreadCount > 99 ? '99+' : botlessUnreadCount}
                                </span>
                              )}
                            </div>
                          )
                        }
                      ]}
                      activeId={botStatusFilter}
                      onChange={(id) => setBotStatusFilter(id as any)}
                      size="xs"
                      variant={theme}
                      iconOnly={true}
                      className="w-full justify-center"
                      tabMinWidth="0px"
                    />
                  </div>

                  {showFilter && (
                    <div data-ui="chat-sidebar-advanced-filters" className="mt-3 border-none outline-none rounded-md p-3 bg-black/20">
                      {/* Platform (stub: instagram only) */}
                      <div data-ui="chat-sidebar-region-065" className="flex items-center gap-3 mb-3">
                        <span className="text-xs text-white/60">{t('chatSidebar.platform')}</span>
                        <label className="text-xs flex items-center gap-1 cursor-pointer">
                          <input type="radio" checked={platformFilter === 'instagram'} onChange={() => setPlatformFilter('instagram')} className="focus:outline-none focus:ring-0" />
                          <span>{t('chatSidebar.instagram')}</span>
                        </label>
                        <label className="text-xs flex items-center gap-1 text-white/40 cursor-not-allowed" title={t('chatSidebar.comingSoon')}>
                          <input type="radio" disabled className="focus:outline-none focus:ring-0" />
                          <span>{t('chatSidebar.whatsapp')}</span>
                        </label>
                        <label className="text-xs flex items-center gap-1 text-white/40 cursor-not-allowed" title={t('chatSidebar.comingSoon')}>
                          <input type="radio" disabled className="focus:outline-none focus:ring-0" />
                          <span>{t('chatSidebar.email')}</span>
                        </label>
                      </div>

                      {/* Tags search & list */}
                      <div data-ui="chat-sidebar-region-066" className="mb-2 relative z-[9000]">
                        <GhostSelect
                          options={(() => {
                            const fMap = new Map<number, string>();
                            for (const f of availableFolders) fMap.set(Number(f.id), String(f.name));
                            return [...(availableTags || [])].sort((a, b) => {
                              const fa = fMap.get(Number(a.folder_id || 0)) || '';
                              const fb = fMap.get(Number(b.folder_id || 0)) || '';
                              return fa.localeCompare(fb) || String(a.name || '').localeCompare(String(b.name || ''));
                            }).map(tag => {
                              const folderName = fMap.get(Number(tag.folder_id || 0));
                              return {
                                value: String(tag.id),
                                label: <TagBadge tag={tag} size="sm" folderName={folderName} />,
                                textLabel: String(tag.name || ''),
                              };
                            });
                          })()}
                          value={selectedTagIds.map(String)}
                          onChange={(val: string[]) => setSelectedTagIds(val.map(Number))}
                          multiple={true}
                          searchable={true}
                          searchPlaceholder={t('chatSidebar.tagsSearchPlaceholder')}
                          placeholder={t('chatSidebar.tagsSearchPlaceholder')}
                          className="w-full text-xs"
                        />
                      </div>

                      {/* Selected tag chips */}
                      {selectedTagIds.length > 0 && (
                        <div data-ui="chat-sidebar-selected-tags" className="flex flex-wrap gap-1 mt-2">
                          {selectedTagIds.map(id => {
                            const tagItem = availableTags.find(x => Number(x.id) === Number(id));
                            if (!tagItem) return null;
                            return (
                              <TagBadge
                                key={id}
                                tag={tagItem}
                                size="xs"
                              >
                                <button
                                  className="ml-1 w-3.5 h-3.5 rounded-full flex items-center justify-center hover:bg-white/20 text-white/70 hover:text-white transition-colors leading-none text-[10px]"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTagIds(prev => prev.filter(x => x !== id));
                                  }}
                                  title={t('chatSidebar.buttons.remove')}
                                >
                                  ×
                                </button>
                              </TagBadge>
                            );
                          })}
                          <button
                            type="button"
                            className="text-xs px-2 py-0.5 border rounded hover:bg-black/10"
                            onClick={() => setSelectedTagIds([])}
                          >
                            {t('chatSidebar.clear')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Threads */}
                <div data-ui="chat-sidebar-thread-results" className="divide-y">
                  {threadsLoadFailed && !threadsLoading ? (
                    <div role="alert" className="p-6 text-center">
                      <p>{t('chatSidebar.loadFailed', { defaultValue: 'No se pudieron cargar las conversaciones.' })}</p>
                      <button type="button" className="btn-taratic btn-ghost-dark mt-3" onClick={() => dispatch(refreshThreadsThunk())}>
                        {t('chatSidebar.retryLoad', { defaultValue: 'Reintentar' })}
                      </button>
                    </div>
                  ) : threadsLoading || lastThreadsRefreshAt === 0 ? (
                    <>
                      <ThreadSkeleton />
                      <ThreadSkeleton />
                      <ThreadSkeleton />
                    </>
                  ) : filteredThreads.length === 0 ? (
                    <>
                      {searchQuery ? (
                        <div data-ui="chat-sidebar-region-067" className="p-8 text-center text-white/50">
                          {t('chatSidebar.noConversationsFound')}
                        </div>
                      ) : !channelsStatus.instagram ? (
                        <div data-ui="chat-sidebar-region-068" className="p-6 m-3 border rounded-lg bg-pink-50 border-pink-200 text-pink-900 text-center">
                          <div data-ui="chat-sidebar-region-069" className="flex items-center justify-center gap-2 mb-2">
                            <span className="w-10 h-10 rounded-md bg-pink-100 text-pink-600 flex items-center justify-center text-xl">
                              <i className="fa-brands fa-instagram" aria-hidden="true"></i>
                            </span>
                            <h3 className="text-lg font-semibold">{t('chatSidebar.connectInstagramTitle')}</h3>
                          </div>
                          <p className="text-sm text-pink-800">
                            {t('chatSidebar.connectInstagramHint')}
                          </p>
                          <div data-ui="chat-sidebar-region-070" className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={handleConnectInstagram}
                              className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors"
                              title={t('chatSidebar.connectInstagramTitle')}
                            >
                              {t('chatSidebar.connectInstagramCta')}
                            </button>
                            <button
                              type="button"
                              onClick={() => { try { window.location.href = '/connections'; } catch { window.location.assign('/connections'); } }}
                              className="border border-pink-300 text-pink-700 font-bold py-2 px-4 rounded-lg hover:bg-pink-100 transition-colors"
                              title={t('chatSidebar.goToConnections')}
                            >
                              {t('chatSidebar.goToConnections')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div data-ui="chat-sidebar-region-071" className="p-8 text-center text-white/50">
                          {t('chatSidebar.noConversations')}
                        </div>
                      )}
                    </>
                  ) : (
                    filteredThreads.map((thread) => {
                      const rowTags: Tag[] = Array.isArray((thread as any).tags) ? (thread as any).tags : [];
                      return (
                        <ThreadItem
                          key={thread.id}
                          thread={thread}
                          isSelected={thread.id === selectedThreadId}
                          isPinned={pinnedIds.has(thread.id)}
                          disabled={false}
                          botMeta={threadBotMeta[thread.id]}
                          channelDefaultBotId={channelDefaultBotId}
                          tags={rowTags}
                          onSelect={() => {
                            // Always allow switching; reducer handles concurrency
                            selectThread(thread.id);
                            setShowMobileThreads(false);
                          }}
                          onTogglePin={() => handleTogglePin(thread.id)}
                        />
                      );
                    })
                  )}
                  {(searchQuery.trim() ? searchHasMore : threadsHasMore) && (
                    <div data-ui="chat-sidebar-load-more-threads" ref={threadsLoadMoreSentinelRef} className="flex justify-center py-3 text-xs text-white/50" aria-live="polite">
                      {(searchQuery.trim() ? searchLoadingMore : threadsLoadingMore) ? <i className="fa-solid fa-spinner fa-spin" aria-label="Cargando más conversaciones" /> : null}
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Messages area: mobile navigation replaces the conversation, rather than covering it. */}
          {selectedThreadId && (deviceType === "desktop" || !showMobileThreads) && (
            <div data-ui="chat-sidebar-conversation" className="flex-1 flex flex-col bg-black/60 min-h-0 min-w-0 overflow-x-hidden">
              <AppModal open={galleryOpen} onClose={() => setGalleryOpen(false)} title="Archivos de la conversación">
                <p className="mb-4 text-sm">Archivos de los mensajes cargados. Carga mensajes anteriores en el chat para ver más.</p>
                <div className="grid gap-4 sm:grid-cols-2">{safeMessages.filter(m => m.imageUrl || m.attachments?.length).map(m => <div key={m.id} className="rounded-xl border p-3"><MessageMedia message={m} /></div>)}</div>
                {!safeMessages.some(m => m.imageUrl || m.attachments?.length) && <p>No hay archivos en los mensajes cargados.</p>}
              </AppModal>
              {mediaOpen && <MediaComposer key={selectedThreadId} open onClose={() => setMediaOpen(false)} threadId={selectedThreadId} blocked={selectedReplyWindowBlocked} />}
              {/* Chat header */}
              <div data-header-collapsed={headerCollapsed} data-ui="chat-sidebar-conversation-header" className="flex items-center gap-2 p-4 bg-black/75 border-b border-white/10">
                <BtnTaratic type="button" size="xs" data-ui="chat-header-files" className="shrink-0" onClick={() => setGalleryOpen(true)}>Archivos</BtnTaratic>
                <BtnTaratic
                  type="button"
                  iconOnly
                  data-ui="chat-header-toggle"
                  className="order-last shrink-0 self-start"
                  aria-label={headerCollapsed ? 'Ampliar cabecera' : 'Minimizar cabecera'}
                  aria-expanded={!headerCollapsed}
                  onClick={() => { setHeaderCollapsed(v => !v); setShowBotMenu(false); }}
                >
                  <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 transition-transform ${headerCollapsed ? '' : 'rotate-180'}`} />
                </BtnTaratic>
                {(deviceType === "mobile" || deviceType === "tablet") && (
                  <BtnTaratic
                    data-ui="chat-header-back"
                    type="button"
                    color="ghost-dark"
                    iconOnly
                    onClick={() => { setShowBotMenu(false); setShowResolveModal(false); setShowMobileThreads(true); }}
                    aria-label="Volver a conversaciones"
                    title="Volver a conversaciones"
                    className="shrink-0"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </BtnTaratic>
                )}
                {selectedThread?.avatarUrl ||
                  safeMessages.find((m) => !m.fromMe && m.avatarUrl)
                    ?.avatarUrl ? (
                  <img
                    src={
                      (selectedThread?.avatarUrl ||
                        safeMessages.find((m) => !m.fromMe && m.avatarUrl)
                          ?.avatarUrl) as string
                    }
                    alt={(selectedThread?.name || t('orderDetail.labels.client')) as string}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div data-ui="chat-sidebar-region-072" className="w-10 h-10 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                    {(selectedThread?.name || "C").charAt(0).toUpperCase()}
                  </div>
                )}
                <div data-ui="chat-sidebar-conversation-identity" className="flex-1 relative">
                  <div data-ui="chat-sidebar-region-073" className="flex items-center gap-2 flex-wrap">
                    <h3 data-ui="chat-thread-header-name" className="font-semibold">
                      {selectedThread?.name || t('orderDetail.labels.client')}
                    </h3>
                    {selectedIsHumanIntervention ? (
                      <button
                        data-ui="chat-thread-human-intervention-badge"
                        onClick={() => setShowResolveModal(true)}
                        className="ml-2 text-[10px] font-bold px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-500 border border-yellow-500/30 flex items-center gap-1.5 shadow-[0_0_8px_rgba(234,179,8,0.3)] hover:bg-yellow-500/30 transition-colors"
                        title={t('chatSidebar.thread.resolveHumanInterventionTitle')}
                      >
                        <FontAwesomeIcon icon={faUser} className="w-3 h-3" />
                        {t('chatSidebar.thread.botPausedHumanIntervention')}
                      </button>
                    ) : (
                      <button
                        ref={botMenuAnchorRef}
                        data-ui="chat-thread-header-bot-selector"
                        aria-expanded={showBotMenu}
                        data-bot-status={selectedIsBotless ? "off" : "active"}
                        onClick={() => {
                          setShowBotMenu(!showBotMenu);
                          if (!showBotMenu) void fetchEffective();
                        }}
                        className={`ml-2 cursor-pointer ${selectedIsBotless
                          ? "border border-orange-500/40 bg-orange-500/20 text-orange-300 shadow-[0_0_8px_rgba(249,115,22,0.35)] hover:bg-orange-500/30"
                          : ACTIVE_THREAD_BOT_TAG_CLASSES
                          }`}
                        title={selectedIsBotless ? t('chatSidebar.bot.offNoBot') : t('chatSidebar.bot.threadMenuTitle')}
                      >
                        {selectedIsBotless ? (
                          <FontAwesomeIcon icon={faPowerOff} className="w-3 h-3" />
                        ) : (
                          <span aria-hidden="true">🤖</span>
                        )}
                        <span className="truncate max-w-[100px] sm:max-w-[120px] md:max-w-[150px] lg:max-w-[180px] font-semibold">
                          {selectedIsBotless
                            ? t('chatSidebar.bot.offNoBot')
                            : effectiveInfo?.effective_bot
                            ? `${effectiveInfo.effective_bot.name} · ${effectiveInfo.effective_bot.pricing_type || "base"}`
                            : activeByChannel.instagram
                              ? `${activeByChannel.instagram.name} · ${activeByChannel.instagram.pricing_type || "base"}`
                              : t('chatSidebar.common.notDefined')}
                        </span>
                        <svg
                          className={`w-3 h-3 transition-transform ${showBotMenu ? "rotate-180" : ""}`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.085l3.71-3.855a.75.75 0 111.08 1.04l-4.24 4.41a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )}

                  </div>
                  <div data-ui="chat-sidebar-region-074">
                    {(selectedThread as any)?.appointment_booked && (
                      <BtnTaratic
                        data-ui="chat-thread-appointment-badge"
                        color="green"
                        size="xs"
                        onClick={() => saveThreadOverride({ appointment_booked: false })}
                        disabled={savingOverride}
                        className="ml-2 flex items-center gap-1.5"
                        title={t('chatSidebar.thread.markAppointmentReviewedTitle')}
                      >
                        <FontAwesomeIcon icon={faCalendarCheck} className="w-3 h-3" />
                        {savingOverride ? t('chatSidebar.common.saving') : t('chatSidebar.thread.appointmentReviewed')}
                      </BtnTaratic>
                    )}
                    {selectedReplyWindowBlocked && (
                      <span
                        data-ui="chat-thread-header-blocked-badge"
                        className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-red-400/40 bg-red-500/20 px-3 py-1 text-[10px] font-bold tracking-wide text-red-300 shadow-[0_0_8px_rgba(248,113,113,0.25)]"
                        title={t('chatSidebar.replyWindow.blockedDescription')}
                      >
                        <FontAwesomeIcon icon={faLock} className="h-3 w-3" />
                        {t('chatSidebar.replyWindow.blocked')}
                      </span>
                    )}

                    {selectedThread?.username && (
                      <span className="text-sm text-white/50 mr-2">
                        @{selectedThread.username}
                      </span>
                    )}
                    {selectedThread?.patient && (
                      <div data-ui="chat-thread-header-patient" className="inline-flex items-center gap-2 mt-1 text-xs text-blue-300 bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-500/20">
                        <FontAwesomeIcon icon={faUser} />
                        <span className="font-medium">{selectedThread.patient.full_name}</span>
                        {selectedThread.patient.phone && (
                          <span data-ui="chat-thread-header-patient-phone" className="text-white/60 ml-1">📞 {selectedThread.patient.phone}</span>
                        )}
                      </div>
                    )}
                    {/* Render nothing here since the yellow badge is the main indicator now */}
                  </div>
                  {/* Assigned tags (selected thread) and Thread Actions */}
                  {selectedThreadId && (
                    <div data-ui="chat-sidebar-region-076" className="mt-1 mb-2 flex flex-col gap-2">
                      {threadTags.length > 0 && (
                        <div data-ui="chat-sidebar-region-077" className="flex flex-wrap gap-1">
                          {threadTags.map((tag: any) => (
                            <TagBadge key={tag.id || tag.tag_id} tag={tag} size="sm" />
                          ))}
                        </div>
                      )}
                      <div data-ui="chat-sidebar-region-078" className="flex flex-wrap items-center gap-2">
                        <ThreadTagsSelector threadId={selectedThreadId} assignedTags={threadTags} />
                        <div data-ui="chat-sidebar-region-079" className="flex items-center gap-2">
                          <ThreadNotesPanel threadId={selectedThreadId} />
                          {selectedThread && selectedThread.customer_id && (
                            <PatientLinkPanel
                              threadId={selectedThreadId}
                              customerId={selectedThread.customer_id}
                              chatChannel="instagram"
                              currentPatientId={selectedThread.patient?.id}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {showBotMenu && (
                    <ChatBotPopover anchorRef={botMenuAnchorRef} theme={theme} label={t('chatSidebar.bot.threadMenuTitle')}>
                      <div data-ui="chat-sidebar-region-080" className="flex items-center justify-between">
                        <BtnTaratic type="button" color="ghost-dark" iconOnly size="xs" className="order-last shrink-0" aria-label={t('chatSidebar.buttons.close', { defaultValue: 'Cerrar' })} onClick={() => setShowBotMenu(false)}>
                          <FontAwesomeIcon icon={faTimes} />
                        </BtnTaratic>
                        <div data-ui="chat-sidebar-region-081" className="text-sm font-medium text-white/70 flex items-center gap-2">
                          <i className="fa-brands fa-instagram" aria-hidden="true"></i>
                          <span>{t('chatSidebar.instagram')}</span>
                        </div>
                        <BtnTaratic
                          onClick={() => void fetchEffective()}
                          color="ghost-dark"
                          size="xs"
                          className="btn-compact-sm"
                          title={t('chatSidebar.buttons.refresh')}
                        >
                          {t('chatSidebar.buttons.refresh')}
                        </BtnTaratic>
                      </div>
                      <div data-ui="chat-sidebar-region-082" className="mt-2.5 grid grid-cols-1 gap-2.5 text-xs">
                        <div data-ui="chat-sidebar-region-083">
                          <div data-ui="chat-sidebar-region-084" className="text-white font-bold">{t('chatSidebar.bot.effective.label')}</div>
                          <div data-ui="chat-sidebar-region-085" className="text-white/80 flex items-center gap-2 mt-0.5">
                            <span>
                              {effectiveInfo?.bot_enabled === false
                                ? t('chatSidebar.bot.offNoBot')
                                : effectiveInfo?.effective_bot
                                  ? `${effectiveInfo.effective_bot.name} · ${effectiveInfo.effective_bot.bot_type || "general"} · ${effectiveInfo.effective_bot.pricing_type || "base"}`
                                  : "—"}
                            </span>
                            {effectiveInfo?.effective_bot && (
                              <BtnTaratic
                                onClick={() =>
                                  goToBotConfig(effectiveInfo.effective_bot.id)
                                }
                                color="ghost-dark"
                                size="xs"
                                className="text-[9px] h-auto min-h-0 py-0.5 px-1.5"
                                title={t('chatSidebar.bot.effective.configureTitle')}
                              >
                                {t('chatSidebar.buttons.configure')}
                              </BtnTaratic>
                            )}
                          </div>
                        </div>
                        <div data-ui="chat-sidebar-region-086" className="flex items-center gap-2">
                          <span className="text-white font-bold">{t('chatSidebar.bot.override.label')}</span>
                          <GhostSelect
                            className="flex-1 max-w-[200px]"
                            disabled={botsLoading || savingOverride}
                            value={effectiveInfo?.selected_bot_id ? String(effectiveInfo?.selected_bot_id) : ""}
                            onChange={async (val: string) => {
                              const id = val ? Number(val) : NaN;
                              await saveThreadOverride({
                                selected_bot_id: Number.isFinite(id) ? id : null,
                              });
                            }}
                            options={[
                              { value: "", label: t('chatSidebar.bot.override.noneOption') || "Usar Bot de Sistema" },
                              ...(bots || [])
                                .filter((b) => !b.channels || b.channels.includes("instagram"))
                                .map((b) => ({
                                  value: String(b.id),
                                  label: `${b.name} (tipo: ${b.bot_type || "general"})`
                                }))
                            ]}
                          />
                        </div>
                        <div data-ui="chat-sidebar-region-087" className="flex items-center justify-between">
                          <div data-ui="chat-sidebar-region-088" className="flex items-center gap-2">
                            <span className="text-xs text-white/60">
                              {t('chatSidebar.bot.enabledThreadLabel')}
                            </span>
                            <GhostSwitch
                              variant={theme}
                              size="sm"
                              disabled={savingOverride}
                              checked={!!(effectiveInfo?.bot_enabled ?? true)}
                              onChange={async (val) => {
                                await saveThreadOverride({
                                  bot_enabled: val,
                                });
                              }}
                            />
                            <span className="text-xs text-white/80 font-medium">
                              {(effectiveInfo?.bot_enabled ?? true)
                                ? t('chatSidebar.common.on')
                                : t('chatSidebar.common.off')}
                            </span>
                          </div>
                          {savingOverride && (
                            <span className="text-xs text-white/50">
                              {t('chatSidebar.common.saving')}
                            </span>
                          )}
                        </div>
                        {isDemoModeActive() && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-xl border border-white/10 bg-black/20 p-2">
                            <GhostSwitch
                              variant={theme}
                              size="sm"
                              disabled={savingOverride}
                              checked={selectedIsHumanIntervention}
                              onChange={(val) => saveThreadOverride({ escalated_to_human: val })}
                              label={t('chatSidebar.thread.humanIntervention')}
                            />
                            <GhostSwitch
                              variant={theme}
                              size="sm"
                              disabled={savingOverride}
                              checked={!!selectedThread?.appointment_booked}
                              onChange={(val) => saveThreadOverride({ appointment_booked: val })}
                              label={t('chatSidebar.thread.appointmentScheduledByAI')}
                            />
                          </div>
                        )}
                        <div data-ui="chat-sidebar-region-089" className="text-[11px] text-white/80 flex items-center gap-2">
                          <span>
                            <span className="text-white font-bold">{t('chatSidebar.bot.channelDefault.label')}</span>{" "}
                            {activeByChannel.instagram
                              ? `${activeByChannel.instagram.name} · ${activeByChannel.instagram.pricing_type || "base"}`
                              : t('chatSidebar.common.notDefined')}
                          </span>
                          {activeByChannel.instagram && (
                            <BtnTaratic
                              onClick={() =>
                                goToBotConfig(activeByChannel.instagram!.id)
                              }
                              color="ghost-dark"
                              size="xs"
                              className="text-[9px] h-auto min-h-0 py-0.5 px-1.5"
                              title={t('chatSidebar.channels.configureDefaultBotTitle')}
                            >
                              {t('chatSidebar.buttons.configure')}
                            </BtnTaratic>
                          )}
                        </div>
                      </div>
                    </ChatBotPopover>
                  )}

                  {showResolveModal && (
                    <div data-ui="chat-sidebar-reactivate-bot-dialog" className="absolute z-10 right-0 top-14 w-full max-w-[95vw] sm:w-[320px] bg-black/90 backdrop-blur-md border border-white/20 rounded-xl shadow-2xl p-5">
                      <BtnTaratic
                        type="button"
                        color="ghost-dark"
                        iconOnly
                        size="xs"
                        onClick={() => setShowResolveModal(false)}
                        className="absolute top-3 right-3"
                        aria-label={t('chatSidebar.buttons.close', { defaultValue: 'Cerrar' })}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </BtnTaratic>
                      <div data-ui="chat-sidebar-region-090" className="flex flex-col items-center text-center mb-4">
                        <div data-ui="chat-sidebar-region-091" className="w-10 h-10 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center mb-3">
                          <FontAwesomeIcon icon={faRobot} className="w-5 h-5" />
                        </div>
                        <h3 className="text-md font-bold text-white mb-1">Reactivar Chatbot</h3>
                        <p className="text-xs text-white/70">
                          Selecciona el chatbot que se encargará de continuar esta conversación.
                        </p>
                      </div>

                      <div data-ui="chat-sidebar-region-092" className="mb-5">
                        <label className="block text-[11px] font-semibold text-white/60 mb-2">
                          Chatbot Asignado
                        </label>
                        <GhostSelect
                          className="w-full text-xs"
                          disabled={botsLoading || savingOverride}
                          value={resolveSelectedBot}
                          onChange={(val: string) => setResolveSelectedBot(val)}
                          options={[
                            { value: "", label: "Usar Bot por Defecto del Canal" },
                            ...(bots || [])
                              .filter((b) => !b.channels || b.channels.includes("instagram"))
                              .map((b) => ({
                                value: String(b.id),
                                label: `${b.name} (tipo: ${b.bot_type || "general"})`
                              }))
                          ]}
                        />
                      </div>

                      <div data-ui="chat-sidebar-region-093" className="flex gap-2">
                        <BtnTaratic
                          className="flex-1 text-xs py-1.5"
                          onClick={() => setShowResolveModal(false)}
                          color="ghost-dark"
                        >
                          Cancelar
                        </BtnTaratic>
                        <BtnTaratic
                          className="flex-1 text-xs py-1.5"
                          disabled={savingOverride}
                          onClick={async () => {
                            const id = resolveSelectedBot ? Number(resolveSelectedBot) : null;
                            await saveThreadOverride({
                              selected_bot_id: id,
                              bot_enabled: true,
                            });
                            setShowResolveModal(false);
                          }}
                          color="blue"
                        >
                          {savingOverride ? 'Reactivando...' : 'Reactivar'}
                        </BtnTaratic>
                      </div>
                    </div>
                  )}
                  {typingThreads.has(String(selectedThreadId || '')) && (
                    <p className="text-sm text-white/50 italic">
                      {t('chatSidebar.thread.typing')}...
                    </p>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div
                data-ui="chat-sidebar-messages"
                className="flex-1 overflow-y-auto p-4 min-h-0"
                style={{ overflowAnchor: "none" }}
                ref={messagesContainerRef}
                onScroll={handleScroll}
              >
                {loadingOlder && (
                  <div data-ui="chat-sidebar-region-094" className="flex justify-center my-2">
                    <i className="fa-solid fa-spinner fa-spin text-white/50 text-xl"></i>
                  </div>
                )}
                {messagesLoading && groupedMessages.length === 0 ? (
                  <>
                    <MessageSkeleton />
                    <MessageSkeleton />
                    <MessageSkeleton />
                  </>
                ) : safeMessages.length === 0 ? (
                  <div data-ui="chat-sidebar-region-095" className="text-center text-white/50 py-8">
                    <p>{t('chatSidebar.noMessages')}</p>
                    <p className="text-sm mt-2">
                      {t('chatSidebar.sendOneToStart')}
                    </p>
                  </div>
                ) : (
                  <>
                    {groupedMessages.map((msg) => {
                      if (msg.isDateSeparator) {
                        return (
                          <div data-ui="chat-message-date-separator" key={msg.id} className="flex justify-center my-4">
                            <span className="px-3 py-1 bg-black/75 text-white/70 text-[11px] font-medium rounded-full shadow-sm backdrop-blur-md border border-white/5">
                              {msg.dateString}
                            </span>
                          </div>
                        );
                      }
                      return (
                        <React.Fragment key={msg.id}>
                          {/* New messages separator - only show for unread messages from others */}
                          {firstNewMessageId &&
                            msg.id === firstNewMessageId &&
                            !msg.fromMe && (
                              <div data-ui="chat-new-messages-marker" id="new-messages-marker" className="flex items-center my-4">
                                <div data-ui="chat-sidebar-region-096" className="flex-1 border-t border-blue-300" />
                                <span className="mx-3 text-xs text-blue-600 bg-blue-50 border border-blue-300 rounded-full px-3 py-1 font-medium shadow-sm">
                                  {t('chatSidebar.newMessages')}
                                </span>
                                <div data-ui="chat-sidebar-region-097" className="flex-1 border-t border-blue-300" />
                              </div>
                            )}
                          <MessageItem
                            message={msg}
                            showAvatar={msg.showAvatar}
                            isLastFromSender={msg.isLastFromSender}
                            threadAvatarUrl={selectedThread?.avatarUrl}
                            threadName={selectedThread?.name}
                          />
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
                <div data-ui="chat-sidebar-messages-end" ref={messagesEndRef} />
              </div>

              {/* Message composer */}
              <div data-ui="chat-sidebar-composer" className="relative p-4 bg-black/20 border-t">
                {/* IA Drafts panel (opens upward) */}
                {showAIDrafts && (
                  <div data-ui="chat-sidebar-ai-drafts" className="absolute bottom-full left-0 right-0 mx-2 mb-2 lg:mb-3 min-h-[200px] max-h-[70vh] flex flex-col bg-gray-900/60 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl p-3 z-20 max-w-full">
                    <div data-ui="chat-sidebar-region-098" className="flex justify-end mb-1 flex-shrink-0">
                      <BtnTaratic
                        type="button"
                        onClick={() => setShowAIDrafts(false)}
                        color="ghost-dark"
                        iconOnly
                        className="!w-7 !h-7 !text-xs z-30"
                        title={t('chatSidebar.buttons.close')}
                        aria-label={t('chatSidebar.buttons.close')}
                      >
                        <i className="fa-solid fa-times"></i>
                      </BtnTaratic>
                    </div>
                    <div data-ui="chat-sidebar-region-099" className="flex items-center gap-2 mb-2 flex-wrap pr-2 flex-shrink-0">
                      <span className="text-sm font-medium text-white/50">{t('chatSidebar.ai.botLabel')}</span>
                      <GhostSelect
                        className="min-w-[180px] flex-1 max-w-full [&>button]:!py-0.5 [&>button]:!h-7 [&>button]:!px-3 [&>button]:!text-xs [&>button]:!min-h-0"
                        disabled={botsLoading}
                        value={aiSelectedBotId ? String(aiSelectedBotId) : ""}
                        onChange={(val: string) => {
                          setAISelectedBotId(
                            val ? Number(val) : ""
                          );
                          const activeBot = bots?.find(b => String(b.id) === String(val));
                          setAIUsedBotName(activeBot ? activeBot.name : "");
                        }}
                        options={[
                          { value: "", label: t('chatSidebar.ai.defaultBot') || "Bot Efectivo Actual" },
                          ...(bots || [])
                            .filter((b) => !b.channels || b.channels.includes("instagram"))
                            .map((b) => ({
                              value: String(b.id),
                              label: `${b.name} · ${b.bot_type || "general"} · ${b.pricing_type || "base"}`
                            }))
                        ]}
                      />
                      <div data-ui="chat-sidebar-region-100" className="ml-auto flex items-center gap-2 flex-shrink-0">
                        <BtnTaratic
                          type="button"
                          onClick={fetchAIDrafts}
                          color="purple"
                          size="sm"
                          disabled={aiDraftsLoading}
                          title={t('chatSidebar.ai.generateSuggestionsTitle')}
                          className="flex items-center gap-1 font-medium !bg-purple-700 hover:!bg-purple-800"
                        >
                          <span aria-hidden="true">✨</span>
                          <span>
                            {aiDraftsLoading ? t('chatSidebar.ai.generating') : t('chatSidebar.ai.generate')}
                          </span>
                        </BtnTaratic>
                      </div>
                    </div>
                    {aiError && (
                      <div data-ui="chat-sidebar-region-101" className="text-xs text-red-400 bg-red-400/10 p-2 rounded border border-red-400/20 mb-2 flex-shrink-0">{aiError}</div>
                    )}
                    <div data-ui="chat-sidebar-region-102" className="grid gap-2 overflow-y-auto flex-1 min-h-0">
                      {aiDraftsLoading ? (
                        <div data-ui="chat-sidebar-region-103" className="text-sm text-white/50 animate-pulse text-center py-4">
                          {t('chatSidebar.ai.generating')}
                        </div>
                      ) : (
                        aiSuggestions.map((s, idx) => (
                          <div
                            data-ui={`chat-sidebar-ai-draft-${idx + 1}`}
                            key={idx}
                            className="p-2 rounded hover:bg-black/10 shadow-[inset_0_0_15px_rgba(0,0,0,0.5)] bg-black/20"
                          >
                            <div data-ui="chat-sidebar-region-104" className="text-[11px] text-white/60 mb-1 flex items-center justify-between gap-2">
                              <div data-ui="chat-sidebar-region-105" className="min-w-0 flex items-center gap-1">
                                <span className="font-medium">
                                  {(() => {
                                    const activeBot = bots?.find(b => String(b.id) === String(aiSelectedBotId));
                                    const botName = activeBot ? activeBot.name : aiUsedBotName;
                                    return botName ? `Respuesta de ${botName}` : t('chatSidebar.ai.botResponseLabel');
                                  })()}
                                </span>
                                <span>:</span>
                              </div>
                              <div data-ui="chat-sidebar-region-106" className="flex items-center gap-1 flex-shrink-0">
                                <BtnTaratic
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(
                                        s || ""
                                      );
                                    } catch { }
                                  }}
                                  color="ghost-dark"
                                  size="xs"
                                  className="flex items-center gap-1"
                                  title={t('chatSidebar.ai.copyToClipboardTitle')}
                                  aria-label={t('chatSidebar.ai.copyToClipboardTitle')}
                                >
                                  <i
                                    className="fa fa-copy"
                                    aria-hidden="true"
                                  ></i>
                                  <span>{t('chatSidebar.ai.copy')}</span>
                                </BtnTaratic>
                                <BtnTaratic
                                  type="button"
                                  onClick={async () => {
                                    if (selectedReplyWindowBlocked) return;
                                    try {
                                      const activeBot = bots?.find(b => String(b.id) === String(aiSelectedBotId));
                                      const bName = activeBot ? activeBot.name : aiUsedBotName;
                                      await sendMessage(s || "", undefined, {
                                        senderKind: "bot",
                                        origin: "taratic_api",
                                        botName: bName || "generic"
                                      });
                                      setShowAIDrafts(false);
                                    } catch (err) {
                                      console.error("Error sending AI draft", err);
                                    }
                                  }}
                                  color="blue"
                                  size="xs"
                                  disabled={selectedReplyWindowBlocked}
                                  className="flex items-center gap-1"
                                  title={selectedReplyWindowBlocked
                                    ? t('chatSidebar.replyWindow.blockedDescription')
                                    : t('chatSidebar.buttons.sendBotSuggestion')}
                                >
                                  <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
                                  <span>{t('chatSidebar.buttons.send')}</span>
                                </BtnTaratic>
                              </div>
                            </div>
                            <div data-ui="chat-sidebar-region-107" className="whitespace-pre-wrap break-words">
                              {s}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
                {/* Options row with better labels */}
                <div data-ui="chat-sidebar-composer-options" className="flex items-center gap-2 mb-2 flex-wrap">
                  <div data-ui="chat-sidebar-region-108" className="flex items-center gap-1 text-xs text-white/60">
                    <span>{t('chatSidebar.sendAs')}</span>
                    <GhostSelect
                      value={senderKind}
                      onChange={(val: string) => {
                        setSenderKind(val as any);
                        // Reset bot name when changing to human
                        if (val === "human") setBotName("");
                      }}
                      className="min-w-[100px] [&>button]:!py-0 [&>button]:!h-7 [&>button]:!px-2 [&>button]:!text-xs [&>button]:!min-h-0"
                      options={[
                        { value: "human", label: t('chatSidebar.sender.human') || "Manual" },
                        { value: "bot", label: t('chatSidebar.sender.bot') || "Bot" },
                        { value: "auto", label: t('chatSidebar.sender.auto') || "Automático" }
                      ]}
                    />
                  </div>

                  {/* Show bot name input when bot is selected */}
                  {senderKind === "bot" && (
                    <div data-ui="chat-sidebar-region-109" className="flex items-center gap-1 text-xs text-white/60">
                      <span>{t('chatSidebar.senderExtras.botNameLabel')}</span>
                      <input
                        type="text"
                        value={botName}
                        onChange={(e) => setBotName(e.target.value)}
                        placeholder={t('chatSidebar.senderExtras.botNamePlaceholder')}
                        className="text-xs bg-white/5 border border-white/10 rounded-lg px-2 h-7 w-24 text-white focus:outline-none focus:ring-1 focus:ring-white/30 transition-colors"
                        title={t('chatSidebar.senderExtras.botNameTitle')}
                      />
                    </div>
                  )}

                  {/* IA quick access button inside the options row */}
                  <BtnTaratic
                    type="button"
                    onClick={() => {
                      const next = !showAIDrafts;
                      setShowAIDrafts(next);
                      if (next && aiSuggestions.length === 0) {
                        fetchAIDrafts();
                      }
                    }}
                    color="purple"
                    size="xs"
                    className="flex items-center gap-1 shadow-md font-medium !h-7 !bg-purple-700 hover:!bg-purple-800"
                    title={t('chatSidebar.ai.generateWithAI')}
                  >
                    <span aria-hidden="true">✨</span>
                    <span>{t('chatSidebar.ai.generateWithAI')}</span>
                  </BtnTaratic>
                  {isDemoModeActive() && (
                    <>
                      <BtnTaratic
                        type="button"
                        data-ui="chat-demo-ai-customer"
                        onClick={() => void handleGenerateDemoRole('customer')}
                        color="indigo"
                        size="xs"
                        disabled={!!demoGeneratingRole}
                        className="flex items-center gap-1 !h-7 text-white shadow-[0_3px_10px_rgba(15,23,42,0.35)]"
                        title={t('chatSidebar.ai.demoCustomerTitle')}
                      >
                        <span aria-hidden="true">👤</span>
                        <span>
                          {demoGeneratingRole === 'customer'
                            ? t('chatSidebar.ai.generating')
                            : t('chatSidebar.ai.demoCustomer')}
                        </span>
                      </BtnTaratic>
                      <BtnTaratic
                        type="button"
                        data-ui="chat-demo-ai-operator"
                        onClick={() => void handleGenerateDemoRole('operator')}
                        color="purple"
                        size="xs"
                        disabled={!!demoGeneratingRole}
                        className="flex items-center gap-1 !h-7 text-white shadow-[0_3px_10px_rgba(15,23,42,0.35)]"
                        title={t('chatSidebar.ai.demoOperatorTitle')}
                      >
                        <span aria-hidden="true">✨</span>
                        <span>
                          {demoGeneratingRole === 'operator'
                            ? t('chatSidebar.ai.generating')
                            : t('chatSidebar.ai.demoOperator')}
                        </span>
                      </BtnTaratic>
                    </>
                  )}
                </div>

                <ReplyWindowCountdown
                  replyWindowEndsAt={selectedReplyWindowEndsAt}
                  platform={selectedThread?.platform}
                  blocked={selectedReplyWindowBlocked}
                />

                {/* Input row */}
                <div data-ui="chat-sidebar-composer-input" className="flex items-center gap-2">
                  <button
                    onClick={handleSendImage}
                    disabled={selectedReplyWindowBlocked}
                    className={`p-2 rounded-lg transition-colors ${selectedReplyWindowBlocked
                      ? 'cursor-not-allowed text-white/30'
                      : 'hover:bg-white/10'
                    }`}
                    title={selectedReplyWindowBlocked
                      ? t('chatSidebar.replyWindow.blockedDescription')
                      : t('chatSidebar.sendImageTitle')}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </button>

                  <textarea
                    ref={messageInputRef}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onInput={(e) => {
                      const el = e.currentTarget;
                      el.style.height = "auto";
                      const h = Math.min(240, Math.max(40, el.scrollHeight));
                      el.style.height = `${h}px`;
                    }}
                    onKeyPress={handleKeyPress}
                    disabled={selectedReplyWindowBlocked}
                    placeholder={selectedReplyWindowBlocked
                      ? t('chatSidebar.replyWindow.inputBlockedPlaceholder')
                      : t('chatSidebar.messagePlaceholder')}
                    className={`flex-1 resize-none rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${selectedReplyWindowBlocked
                      ? 'cursor-not-allowed border-red-400/30 bg-red-950/20 text-white/50'
                      : ''
                    }`}
                    rows={1}
                    style={{ minHeight: "40px", maxHeight: "240px" }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!messageText.trim() || sendingMessage || selectedReplyWindowBlocked}
                    className={`
                      p-2 rounded-lg transition-all
                      ${messageText.trim() && !sendingMessage && !selectedReplyWindowBlocked
                        ? "bg-blue-500 text-white hover:bg-blue-600/80 active:scale-95"
                        : "bg-white/10 text-white/40 cursor-not-allowed"
                      }
                    `}
                  >
                    {sendingMessage ? (
                      <svg
                        className="w-5 h-5 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatSidebar;

